import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Label } from '../../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { SearchableSelect } from '../../../../../components/ui/SearchableSelect';
import {
  Plus,
  Map,
  Table as TableIcon,
  Cable,
  PlusIcon,
  RefreshCcw,
  FileText,
  MoreHorizontal,
  Package,
  Zap,
  Upload,
  Pencil,
} from 'lucide-react';
import { PipeSelector } from '../../../../../components/ui/PipeSelector';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../../../../../components/ui/custom-dropdown-menu';
import { Switch } from '../../../../../components/ui/switch';
import type { DashboardProject } from '../../../types/project';
import type { NDTData, PipeSize } from '../../../types/index';
import { NDTData_List } from '../../../mocks/production';
import { WeldPassList } from '../../../../../components/WeldPassList';
import { ReportGenerationModal } from './ReportGenerationModal';
import { WelderSignatureReportModal } from './WelderSignatureReportModal';
import { AssemblyProduction } from './AssemblyProduction';
import { AddNewProduction } from './AddNewProduction';
import { StatusConfigurationModal } from './StatusConfigurationModal';
import { WeldDataMainTable } from './WeldDataMainTable';
import { WeldDataModal } from './WeldDataModal';
import { AssemblyTable } from './AssemblyTable';
import { PqrReportModal } from './PqrReportModal';
import { useWeldData, useCurrentProject, useProjectsLoader } from '../../../../../redux/WeldData/hooks/useWeldData';
import { useLimitsData } from '../../../../../redux/WeldData/hooks/useLimitsData';
import { clearWeldData, clearAllWeldViews, assignPipeToWeld, renameJobNumber } from '../../../../../redux/WeldData/actions';
import { formatTimestamp, formatDuration } from '../../../../../lib/formatUtils';
import { getUnitFromSettings } from '../../../../../utils/unitUtils';
import { SaveProjectSetupJobNumbers } from '../../../../../redux/Project/setUpSaga';
import {
  getDistinctJobNumbersFromWelds,
  getUpdatedSourcePipeJobNumbers,
  havePipeJobNumbersChanged,
  mergePipeJobNumbers,
} from '../../../utils/pipeJobNumberAssignment';
import { getAllPipeDetails } from '../../../../../redux/Project/setupData/action';
import { GetAllMaterials } from '../../../../../redux/Project/materialSaga';
import weldingImage from '../../../../../assets/Welding.jpg';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { canReportTab, canSignTab, type TeamMemberPermissions } from '../../../utils/permissions';
import { useUserPermissionsAfterLogin } from '../../../../admin/hooks/useUserPermissionsAfterLogin';
import '../../../../../styles/map.css';
import { DeviceManagementModal } from './DeviceManagementModal';
import { Monitor, FileSignature } from 'lucide-react';
import CommonWeldParametersModal from './WeldParamters/CommonWeldParametersModal';
import { ReleaseNotesModal } from '../reports/ReleaseNotesModal';
import { IRNReportsViewModal } from '../reports/IRNReportsViewModal';
import { ProductionReportsViewModal } from '../reports/ProductionReportsViewModal';
import { useProductionData } from '../../../../../redux/ProductionData/hooks/useProductionData';
import { GenerateProductionReportModal } from '../reports/GenerateProductionReportModal';
import { updateProductionMatrailConsumptionStatus } from '../../../../../redux/ProductionData/saga';
import { PumpHouseCoatingTable } from './CoatingPumbHouse/PumpHouseCoatingTable';
import { UploadCoatingPumpHouseDialog } from './CoatingPumbHouse/CoattingDocUploadModal';
import { UploadWeldDataDialog } from './WeldDataUpload/UploadWeldDataDialog';
import { UploadTlogExcelDataDialog } from './WeldDataUpload/UploadTlogExcelDataDialog';
import { RenameJobNumberDialog } from './RenameJobNumberDialog';
import { logJobNumberRenamed, logPipeAssignedToWeld } from '../../../utils/productionAudit';
import { SpoolsProduction } from '../spooltracker/SpoolsProduction';
import { ProjectDashboard } from '../spooltracker/ProjectDashboard';
import { NCRRegisterModal } from '../spooltracker/NCRRegisterModal';

// Moving this to .env throws error
mapboxgl.accessToken =
  "pk.eyJ1IjoibWFuanVuYXRoLWxvZ3ljZW50IiwiYSI6ImNseWd1NGl3ODBibGwybHNhdmFnNjN2YnAifQ._3p7J3DCBjxNHUFDxNQQqQ";

// Global function for weld details button clicks
declare global {
  interface Window {
    handleWeldDetailsClick: (id: string, weldNumber: string, itemData: string) => void;
  }
}

// Removed getData360ProjectId function - now using actual project prop directly

// Production Map Component - Modified to use weld-status data from Redux instead of slog-details API
const ProductionMap: React.FC<{ project: DashboardProject; selectedPipeSize: number; selectedPipe: any; statusCalculationMethod: string; statusCalculationStartedAt?: number | null; canEdit?: boolean }> = ({ project, selectedPipeSize, selectedPipe, statusCalculationMethod, statusCalculationStartedAt = null, canEdit = true }) => {
  const dispatch = useDispatch();
  const { currentProject } = useCurrentProject();
  const { weldData } = useWeldData(); // Keep weldData - still needed for map
  // Removed findProjectByNameAndNumber and projects imports - no longer needed

  // Removed data360ProjectId state - now using actual project prop directly

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [mapError, setMapError] = useState<string | null>(null);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [mapStyle, setMapStyle] = useState<'streets' | 'satellite'>('streets');
  const [is3DView, setIs3DView] = useState(false);

  // State for weld details modal
  const [isWeldDetailsModalOpen, setIsWeldDetailsModalOpen] = useState(false);
  const [selectedWeldData, setSelectedWeldData] = useState<any>(null);
  const [isDeviceManagementModalOpen, setIsDeviceManagementModalOpen] = useState(false);

  // Debug modal state changes
  useEffect(() => {
  }, [isWeldDetailsModalOpen, selectedWeldData]);
  const [isRenderingMarkers, setIsRenderingMarkers] = useState(false);
  const [isOverallLoading, setIsOverallLoading] = useState(true);



  // Function to handle opening weld passes modal (same as WeldDataTable)
  const handleOpenWeldPasses = useCallback((item: any) => {

    // Transform the item data to match the WeldData interface expected by WeldPassList
    const weldData = {
      id: item.id,
      weldNumber: item.weldNumber,
      weldIdentifier: item.weldIdentifier || item.weldNumber,
      wpsNumber: item.wpsNumber || '',
      status: item.status || 'Not Started',
      weldTime: item.weldTime || '-',
      startTime: item.startTime,
      endTime: item.endTime,
      projectId: currentProject?.project?.projectId || item.projectId,
      projectName: currentProject?.project?.projectName || '',
      statusByInstantaneous: item.statusByInstantaneous || 'NULL',
      statusByAvgZone: item.statusByAvgZone || 'NULL',
      statusByAvgPass: item.statusByAvgPass || 'NULL',
      statusByAvgTilt: item.statusByAvgTilt || 'NULL',
      gps: item.gps,
      minDistance: item.minDistance || 0,
      numOfGpsPoints: item.numOfGpsPoints || 0,
      passes: item.passes || [],
      isFileUpload: item.isFileUpload === true || item.is_fileUpload === true,
    };


    setSelectedWeldData(weldData);
    setIsWeldDetailsModalOpen(true);
  }, [currentProject?.project?.projectId, currentProject?.project?.projectName]);

  // Memoize the close handler to prevent re-renders
  const handleCloseWeldDetailsModal = useCallback(() => {
    setIsWeldDetailsModalOpen(false);
    setSelectedWeldData(null);
  }, []);

  // Memoize the assembly number to prevent re-renders
  const assemblyNumber = useMemo(() =>
    selectedWeldData ? `Weld-${selectedWeldData.weldNumber}` : '',
    [selectedWeldData?.weldNumber]
  );

  // Calculate distance between two GPS coordinates using Haversine formula
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Radius of the Earth in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in kilometers
    return distance;
  };

  // Calculate distances between consecutive welds - OPTIMIZED for performance
  const calculateWeldDistances = (data: any[]): any[] => {
    if (data.length < 2) return data;

    return data.map((item, index) => {
      let distanceFromPrevious = 0;

      if (index > 0) {
        // Distance from previous weld
        const prevWeld = data[index - 1];
        distanceFromPrevious = calculateDistance(
          prevWeld.gps.lat, prevWeld.gps.lng,
          item.gps.lat, item.gps.lng
        );
      }

      return {
        ...item,
        distanceFromPrevious
      };
    });
  };

  // Transform weld-status data from Redux to include coordinates - OPTIMIZED for performance
  const productionData = React.useMemo(() => {
    if (!weldData || weldData.length === 0) {
      return [];
    }



    // Process GPS data in a single pass with early filtering
    const transformedData = weldData
      .map((record: any, index: number) => {
        // Early return if no GPS data
        if (!record.gps) {
          return null;
        }

        // Parse GPS data efficiently
        let gpsInfo = null;
        try {
          if (typeof record.gps === 'string') {
            gpsInfo = JSON.parse(record.gps);
          } else if (typeof record.gps === 'object') {
            gpsInfo = record.gps;
          }
        } catch (error) {
          return null;
        }

        // Extract coordinates efficiently
        const latitude = gpsInfo?.Latitude ? parseFloat(gpsInfo.Latitude) : null;
        const longitude = gpsInfo?.Longitude ? parseFloat(gpsInfo.Longitude) : null;

        // Skip records without valid coordinates
        if (!latitude || !longitude) {
          return null;
        }

        // Determine status efficiently
        let status = 'Not Started';
        const statusValue = record.statusByInstantaneous;
        if (statusValue === 'TRUE') {
          status = 'Completed';
        } else if (statusValue === 'FALSE') {
          status = 'Rejected';
        } else if (statusValue === 'EX') {
          status = 'Exception';
        } else if (statusValue === 'CROSS') {
          status = 'Cross';
        }

        // Calculate completion percentage
        const completion = status === 'Completed' ? 100 :
          status === 'Rejected' ? 0 :
            status === 'Exception' ? 75 : 50;

        return {
          id: record.id || index + 1,
          assemblyNumber: `Weld-${record.weldNumber || index + 1}`,
          status,
          stage: 'welding',
          gps: { lat: latitude, lng: longitude },
          weldCount: 1,
          completion,
          // Essential fields only
          weldNumber: record.weldNumber || '',
          wpsNumber: Array.isArray(record.wpsNumber) ? record.wpsNumber[0] || '' : record.wpsNumber || '',
          projectId: record.projectId || 0,
          statusByInstantaneous: statusValue || 'NULL',
          startTime: record.startTime || 0,
          endTime: record.endTime || 0,
          weldTime: record.weldTime || 0
        };
      })
      .filter(Boolean); // Remove null records

    // Calculate distances between welds only if we have data
    if (transformedData.length > 0) {
      return calculateWeldDistances(transformedData);
    }

    return transformedData;
  }, [weldData]); // Only recalculate when weldData changes

  // Memoize the filtered data to prevent unnecessary recalculations
  const validGPSData = React.useMemo(() => {
    return productionData.filter((item: any) => item && item.gps && item.gps.lat && item.gps.lng);
  }, [productionData]);

  // Set up global function for weld details button clicks - OPTIMIZED
  useEffect(() => {
    if (!productionData.length) return;

    window.handleWeldDetailsClick = (id: string, weldNumber: string) => {
      const item = productionData.find((data: any) => data.id.toString() === id);

      if (item) {
        handleOpenWeldPasses(item);
      } else {
      }
    };

    return () => {
      (window as any).handleWeldDetailsClick = undefined;
    };
  }, [productionData]);

  // Removed effect to find data360 project ID - now using actual project prop directly



  // Add markers to map function - HEAVILY OPTIMIZED for performance
  const addMarkersToMap = useCallback(() => {
    // Check if map is ready and GPS data is available
    if (!mapRef.current) {
      return;
    }

    if (!validGPSData || validGPSData.length === 0) {
      return;
    }

    // Get map reference and ensure it's valid
    const map = mapRef.current;
    if (!map) {
      return;
    }

    // Check if map container exists and is mounted
    const container = map.getContainer();
    if (!container || !container.parentNode) {
      return;
    }

    // Check if map style is loaded
    if (!map.isStyleLoaded()) {
      return;
    }

    // Show loading state while rendering markers
    setIsRenderingMarkers(true);

    // Clear existing markers efficiently
    const existingMarkers = document.querySelectorAll('.mapboxgl-marker');
    if (existingMarkers.length > 0) {
      existingMarkers.forEach(marker => marker.remove());
    }

    // Load all markers at once - no density optimization
    const markersToRender = validGPSData;

    // Create all markers at once for immediate display
    const markers: mapboxgl.Marker[] = [];
    const mapBounds = new mapboxgl.LngLatBounds();

    // Process all markers in a single loop
    markersToRender.forEach((item: any) => {
      // Validate GPS coordinates before creating marker
      if (!item.gps || typeof item.gps.lat !== 'number' || typeof item.gps.lng !== 'number') {
        return;
      }

      // Create marker efficiently
      const marker = new mapboxgl.Marker({
        color: getStatusColor(item.status),
      })
        .setLngLat([item.gps.lng, item.gps.lat])
        .setPopup(createMarkerPopup(item));

      markers.push(marker);
      mapBounds.extend([item.gps.lng, item.gps.lat]);
    });

    // Add all markers to map immediately with error handling
    try {
      markers.forEach(marker => {
        if (map && map.getContainer()) {
          marker.addTo(map);
        } else {
        }
      });

      // Fit map to all markers
      if (markers.length > 0 && mapBounds.isEmpty() === false) {
        map.fitBounds(mapBounds, {
          padding: { top: 100, bottom: 100, left: 100, right: 100 },
          maxZoom: 18,
          duration: 1000
        });
      }

      // Hide loading state only after markers are fully rendered and map is fitted
      setTimeout(() => {
        setIsRenderingMarkers(false);
        // Also set isMapLoading to false now that everything is complete
        setIsMapLoading(false);
      }, 500); // Small delay to ensure markers are visible

    } catch (error) {
      // Hide loading state on error too
      setIsRenderingMarkers(false);
      // Also set isMapLoading to false on error
      setIsMapLoading(false);
    }
  }, [validGPSData]);

  // Memoized popup creation function to prevent recreation
  const createMarkerPopup = useCallback((item: any) => {
    const popupHTML = `<div style="padding: 12px; min-width: 280px; font-family: 'Montserrat', sans-serif;">
                           <div style="margin-bottom: 16px;">
                               <div style="display: flex; justify-content: space-between; align-items: center;">
                   <strong style="font-size: 14px; font-weight: 600; color: #00273F;">Weld ${item.weldNumber}</strong>
                   <div style="display: flex; gap: 8px;">
                     <button
                       id="view-details-btn-${item.id}"
                       style="
                         background-color: #EB1C37;
                         color: white;
                         border: none;
                         padding: 6px 12px;
                         border-radius: 8px;
                         font-size: 12px;
                         font-weight: 500;
                         cursor: pointer;
                         transition: background-color 0.15s;
                         display: inline-flex;
                         align-items: center;
                         justify-content: center;
                         min-width: 70px;
                       "
                       onmouseover="this.style.backgroundColor='#C4172E';"
                       onmouseout="this.style.backgroundColor='#EB1C37';"
                       onclick="window.handleWeldDetailsClick('${item.id}', '${item.weldNumber}')"
                       title="View Details"
                     >
                       Details
                     </button>
                     <button
                       id="close-popup-btn-${item.id}"
                       style="
                         background-color: white;
                         color: #464646;
                         border: 1px solid #E2E5EA;
                         padding: 6px 12px;
                         border-radius: 8px;
                         font-size: 12px;
                         font-weight: 500;
                         cursor: pointer;
                         transition: background-color 0.15s;
                         display: inline-flex;
                         align-items: center;
                         justify-content: center;
                         min-width: 70px;
                       "
                       onmouseover="this.style.backgroundColor='#F5F6F8';"
                       onmouseout="this.style.backgroundColor='white';"
                       onclick="this.closest('.mapboxgl-popup').remove()"
                       title="Close"
                     >
                       Close
                     </button>
                   </div>
                 </div>
               </div>

               <div style="margin-bottom: 8px;">
                 <span style="font-size: 12px; color: #969696;">Status:</span>
                 <span style="font-size: 12px; font-weight: 500; margin-left: 8px; color: ${getStatusColor(item.status)};">
                   ${getStatusLabel(item.status)}
                 </span>
               </div>

               <div style="margin-bottom: 8px;">
                 <span style="font-size: 12px; color: #969696;">Start Date:</span>
                 <span style="font-size: 12px; font-weight: 500; margin-left: 8px; color: #464646;">
                   ${item.startTime ? formatTimestamp(Number(item.startTime)) : '-'}
                 </span>
               </div>

               <div style="margin-bottom: 8px;">
                 <span style="font-size: 12px; color: #969696;">End Date:</span>
                 <span style="font-size: 12px; font-weight: 500; margin-left: 8px; color: #464646;">
                   ${item.endTime ? formatTimestamp(Number(item.endTime)) : '-'}
                 </span>
               </div>

               <div style="margin-bottom: 8px;">
                 <span style="font-size: 12px; color: #969696;">Weld Duration:</span>
                 <span style="font-size: 12px; font-weight: 500; margin-left: 8px; color: #464646;">
                   ${item.weldTime ? formatDuration(item.weldTime) : '-'}
                 </span>
               </div>
             </div>`;

    return new mapboxgl.Popup({
      offset: 25,
      anchor: "left",
      closeOnClick: false,
      closeButton: false,
    }).setHTML(popupHTML);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed': return '#10B981';
      case 'Rejected': return '#EF4444';
      case 'Exception': return '#F59E0B';
      case 'Cross': return '#8B5CF6';
      case 'Not Started': return '#6B7280';
      default: return '#6B7280';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'Completed': return 'Completed';
      case 'Rejected': return 'Rejected';
      case 'Exception': return 'Exception';
      case 'Cross': return 'Cross';
      case 'Not Started': return '-';
      default: return 'Unknown';
    }
  };

  // Initialize map when component mounts - OPTIMIZED
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let initializationTimeout: NodeJS.Timeout;

    const initializeMap = async () => {
      setIsMapLoading(true);
      setMapError(null);

      if (typeof mapboxgl === 'undefined') {
        setMapError('Mapbox library not loaded. Please refresh the page.');
        setIsMapLoading(false);
        return;
      }

      if (!mapboxgl.accessToken) {
        setMapError('Mapbox access token is not configured.');
        setIsMapLoading(false);
        return;
      }

      if (!mapContainerRef.current) {
        timeoutId = setTimeout(() => {
          if (!mapContainerRef.current) {
            setMapError('Map container not found. Please refresh the page.');
            setIsMapLoading(false);
          }
        }, 2000);
        return;
      }

      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }

      // Wait for container to have dimensions
      const checkContainerDimensions = () => {
        const container = mapContainerRef.current;
        if (!container) return false;
        const rect = container.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      };

      let attempts = 0;
      const maxAttempts = 20;

      while (!checkContainerDimensions() && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }

      if (!checkContainerDimensions()) {
        setMapError('Map container not properly sized. Please refresh the page.');
        setIsMapLoading(false);
        return;
      }

      try {
        initializationTimeout = setTimeout(() => {
          if (isMapLoading) {
            setMapError('Map initialization timeout. Please try again.');
            setIsMapLoading(false);
          }
        }, 15000);

        mapRef.current = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: mapStyle === 'satellite' ? "mapbox://styles/mapbox/satellite-v9" : "mapbox://styles/mapbox/streets-v12",
          center: [0, 0],
          zoom: 2,
          attributionControl: false,
        });

        mapRef.current.on('load', () => {
          clearTimeout(initializationTimeout);
          // Don't set isMapLoading to false yet - keep it true until markers are rendered
          // setIsMapLoading(false);
          setMapInitialized(true);
          // Add a small delay to ensure map is fully ready
          setTimeout(() => {
            if (mapRef.current && mapRef.current.isStyleLoaded()) {
              addMarkersToMap();
            }
          }, 100);
        });

        mapRef.current.on('style.load', () => {
          if (mapInitialized && mapRef.current && mapRef.current.isStyleLoaded()) {
            // Add a small delay to ensure style is fully loaded
            setTimeout(() => {
              addMarkersToMap();
            }, 100);
          }
        });

        mapRef.current.on('error', (e) => {
          clearTimeout(initializationTimeout);
          setMapError('Failed to load map. Please try again.');
          setIsMapLoading(false);
        });

      } catch (error) {
        clearTimeout(initializationTimeout);
        setMapError('Failed to initialize map. Please try again.');
        setIsMapLoading(false);
      }
    };

    initializeMap();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (initializationTimeout) clearTimeout(initializationTimeout);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [selectedPipeSize, weldData, addMarkersToMap]);

  // Center map on markers when production data changes
  useEffect(() => {
    if (mapInitialized && validGPSData.length > 0 && mapRef.current) {
      setTimeout(() => centerMapOnMarkers(), 100);
    }
  }, [validGPSData, mapInitialized]);

  // Handle retry
  const handleRetry = () => {
    setMapInitialized(false);
    setIsMapLoading(true);
    setMapError(null);

    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    }, 100);
  };

  // Toggle map style between satellite and streets
  const toggleMapStyle = () => {
    if (mapRef.current) {
      const newStyle = mapStyle === 'streets' ? 'satellite' : 'streets';
      setMapStyle(newStyle);

      const styleUrl = newStyle === 'satellite'
        ? 'mapbox://styles/mapbox/satellite-v9'
        : 'mapbox://styles/mapbox/streets-v12';

      mapRef.current.setStyle(styleUrl);
    }
  };

  // Toggle between 2D and 3D view
  const toggle3DView = () => {

    if (!mapRef.current) {
      return;
    }

    if (!is3DView) {
      // Switch to 3D view
      mapRef.current.easeTo({
        pitch: 60,
        bearing: -20,
        duration: 1000
      });
    } else {
      // Switch back to 2D view
      mapRef.current.easeTo({
        pitch: 0,
        bearing: 0,
        duration: 1000
      });
    }

    setIs3DView(!is3DView);
  };

  // Function to center map on markers
  const centerMapOnMarkers = () => {
    if (validGPSData.length > 0 && mapRef.current) {
      const bounds = new mapboxgl.LngLatBounds();

      validGPSData.forEach((item: any) => {
        bounds.extend([item.gps.lng, item.gps.lat]);
      });

      const centerLng = (bounds.getWest() + bounds.getEast()) / 2;
      const centerLat = (bounds.getSouth() + bounds.getNorth()) / 2;

      mapRef.current.setCenter([centerLng, centerLat]);

      mapRef.current.fitBounds(bounds, {
        padding: { top: 100, bottom: 100, left: 100, right: 100 },
        maxZoom: 18,
        duration: 1000
      });
    }
  };

  return (
    <div className="map-container">
      {/* Map Style Toggle Button */}
      <div className="absolute top-4 right-4 z-20">
        <div className="flex flex-col gap-2">
          <button
            onClick={toggleMapStyle}
            className="bg-white px-3 py-2 rounded-lg shadow-md border border-[#E2E5EA] hover:bg-page-bg text-sm font-medium flex items-center gap-2"
          >
            <span>{mapStyle === 'streets' ? '🗺️' : '🛰️'}</span>
            {mapStyle === 'streets' ? 'Satellite' : 'Streets'}
          </button>

          {/* 3D Toggle Button */}
          <button
            onClick={toggle3DView}
            className={`px-3 py-2 rounded-lg shadow-md border text-sm font-medium flex items-center gap-2 transition-all ${is3DView
              ? 'bg-blueprint-blue text-white hover:bg-blueprint-blue/90'
              : 'bg-white text-text-grey border-[#E2E5EA] hover:bg-page-bg'
              }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 7.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7.5" />
              <path d="M21 7.5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2" />
              <path d="M12 12a2 2 0 0 0 0-4" />
              <path d="M3 7.5h18" />
            </svg>
            {is3DView ? '3D View' : '2D View'}
          </button>

        </div>
      </div>

      {/* Custom Navigation Controls */}
      <div className="absolute top-4 left-4 z-20">
        <div className="bg-white rounded-lg shadow-md border border-[#E2E5EA] overflow-hidden">
          <button
            onClick={() => mapRef.current?.zoomIn()}
            className="block w-10 h-10 border-b border-[#E2E5EA] hover:bg-page-bg flex items-center justify-center"
            title="Zoom In"
          >
            <span className="text-lg font-bold">+</span>
          </button>
          <button
            onClick={() => mapRef.current?.zoomOut()}
            className="block w-10 h-10 border-b border-[#E2E5EA] hover:bg-page-bg flex items-center justify-center"
            title="Zoom Out"
          >
            <span className="text-lg font-bold">−</span>
          </button>
          <button
            onClick={centerMapOnMarkers}
            className="block w-10 h-10 hover:bg-page-bg flex items-center justify-center border-t border-[#E2E5EA]"
            title="Center Map on Markers"
          >
            <span className="text-sm">🎯</span>
          </button>
        </div>
      </div>

      {validGPSData.length === 0 && (
        <div className="flex items-center justify-center h-full absolute inset-0 bg-white z-10">
          <div className="text-center">
            <div className="text-heritage-grey mb-2">🗺️</div>
            <p className="text-heritage-grey">No GPS data available</p>
            <p className="text-sm text-heritage-grey mt-2">Please check the API connection</p>
            <button
              onClick={handleRetry}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
            >
              Retry Map
            </button>
          </div>
        </div>
      )}

      {/* Map Loading State - Shows until both map initialization and marker rendering are complete */}
      {(isMapLoading || isRenderingMarkers) && (
        <div className="flex items-center justify-center h-full absolute inset-0 bg-white z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hero-red mx-auto mb-2"></div>
            <p className="text-heritage-grey">
              {isMapLoading ? 'Loading map...' : 'Rendering GPS markers...'}
            </p>
            <p className="text-sm text-heritage-grey mt-2">
              {isMapLoading
                ? `Initializing map with ${validGPSData?.length || 0} weld locations`
                : `Adding ${validGPSData?.length || 0} weld locations to map`
              }
            </p>
          </div>
        </div>
      )}



      {mapError && (
        <div className="flex items-center justify-center h-full absolute inset-0 bg-white z-10">
          <div className="text-center">
            <div className="text-hero-red mb-2">⚠️</div>
            <p className="text-hero-red mb-2">{mapError}</p>
          </div>
        </div>
      )}

      {/* Map Container */}
      <div
        ref={mapContainerRef}
        style={{
          height: "100%",
          width: "100%",
          minHeight: "100%",
          opacity: isMapLoading || isRenderingMarkers || mapError || validGPSData.length === 0 ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out'
        }}
      />

      {/* Weld Details Modal */}
      {selectedWeldData && (
        <WeldPassList
          isOpen={isWeldDetailsModalOpen}
          onClose={handleCloseWeldDetailsModal}
          weld={selectedWeldData}
          assemblyNumber={assemblyNumber}
          statusCalculationMethod={statusCalculationMethod}
          passViewStatusMethod="Instantaneous"
          zoneViewStatusMethod="AvgByZone"
          tiltViewStatusMethod="AvgByTilt"
          statusCalculationStartedAt={statusCalculationStartedAt}
          canEdit={canEdit}
        />
      )}
    </div>
  );
};

// Export ProductionMap component for use in WeldDataModal
export { ProductionMap };

interface ProjectProductionProps {
  project: DashboardProject;
  onViewModeChange?: (mode: 'table' | 'map') => void;
  canEdit?: boolean;
  canReport?: boolean;
}

const isManualWeldingMachineEnabled = (
  welding?: { enabled?: boolean; machines?: { Manual?: { enabled?: boolean } } } | null
): boolean => Boolean(welding?.enabled && welding?.machines?.Manual?.enabled);

/** True when pipeline or any fabrication option has Manual welding enabled. */
const projectHasManualWeldingEnabled = (
  projectTypes?: DashboardProject['project']['projectTypes']
): boolean => {
  if (!projectTypes) return false;

  if (isManualWeldingMachineEnabled(projectTypes.pipeline?.features?.welding)) {
    return true;
  }

  const options = projectTypes.fabrication?.options;
  if (!options) return false;

  return Object.values(options).some((option) =>
    isManualWeldingMachineEnabled(option?.features?.welding)
  );
};

export const ProjectProduction: React.FC<ProjectProductionProps> = ({ project, onViewModeChange, canEdit = true, canReport = true }) => {
  const dispatch = useDispatch();

  // Get user role from useUserPermissionsAfterLogin
  const { userRole, userPermissions, isAdminOrHigher } = useUserPermissionsAfterLogin();

  // Get team member permissions from project
  const teamMemberPermissions = useMemo(() => {
    return (project as any)?.team_member_permissions || (project.project as any)?.team_member_permissions;
  }, [project]);

  // Calculate canSign permission
  const canSign = useMemo(() => {
    // console.log('teamMemberPermissions', teamMemberPermissions);
    //console.log('userRole', userRole);
    // console.log('userPermissions', userPermissions);
    return canSignTab(teamMemberPermissions, 'production', userRole);
  }, [teamMemberPermissions, userRole]);


  // Calculate canSign permission
  const isCustomerRepresentativeUser = useMemo(() => {
    return (project.project as any)?.team_member_role === 'Customer Representative';
  }, [project]);
  const isQCInspectorUser = useMemo(() => {
    return (project.project as any)?.team_member_role === 'QC Inspector' || (project.project as any)?.team_member_role === 'Quality Control' || (project.project as any)?.team_member_role === 'QA/QC';
  }, [project]);
  const isWelderUser = useMemo(() => {
    return (project.project as any)?.team_member_role === 'Welder' || (project.project as any)?.team_member_role === 'Welding';
  }, [project]);

  // Get current user's team member ID from project
  const currentUserTeamMemberId = useMemo(() => {

    //   console.log('userPermissions', userPermissions);
    return userPermissions?.user_id || null;
  }, [userPermissions]);

  // Check if user is admin or super admin for full signature access
  const isAdminOrSuperAdmin = useMemo(() => {
    return isAdminOrHigher();
  }, [isAdminOrHigher]);


  const [isAddingProductionItem, setIsAddingProductionItem] = useState(false);
  const [selectedPipeSize, setSelectedPipeSize] = useState<number>(0);
  const [selectedAssembly, setSelectedAssembly] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [updateProductionMatrailConsumptionStatusLoading, setUpdateProductionMatrailConsumptionStatusLoading] = useState(false);
  const [ndtData, setNDTData] = useState<NDTData[]>(NDTData_List);
  const [selectedForgeId, setSelectedForgeId] = useState<string | null>(null);
  const [selectedPipeId, setSelectedPipeId] = useState<string | null>(null);
  const [distanceFromPipeEnd, setDistanceFromPipeEnd] = useState<number | null>(null);
  const [isQRScannerOpen, setIsQRScannerOpen] = useState(false);
  const [scanningFor, setScanningFor] = useState<'forge' | 'pipe' | null>(null);
  const [isWeldDataModalOpen, setIsWeldDataModalOpen] = useState(false);
  const [selectedAssemblyForWeld, setSelectedAssemblyForWeld] = useState<string | null>(null);
  const [isAssemblyModalOpen, setIsAssemblyModalOpen] = useState(false);
  const [selectedAssemblyForWeldData, setSelectedAssemblyForWeldData] = useState<any>(null);
  const [dateRange, setDateRange] = useState<{ dateFrom: Date | undefined; dateTo: Date | undefined }>({
    dateFrom: undefined,
    dateTo: undefined
  });
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'map'>('table');
  const [isStatusConfigurationModalOpen, setIsStatusConfigurationModalOpen] = useState(false);
  const [isManualWelding, setIsManualWelding] = useState(false);
  const [statusCalculationStartedAt, setStatusCalculationStartedAt] = useState<number | null>(null);
  const [isReportGenerationModalOpen, setIsReportGenerationModalOpen] = useState(false);
  const [isWelderSignatureReportModalOpen, setIsWelderSignatureReportModalOpen] = useState(false);
  const [isDeviceManagementModalOpen, setIsDeviceManagementModalOpen] = useState(false);
  const [isPqrReportModalOpen, setIsPqrReportModalOpen] = useState(false);
  const [pqrWeldNumbers, setPqrWeldNumbers] = useState<string[]>([]);
  const [isWeldDataUploadModalOpen, setIsWeldDataUploadModalOpen] = useState(false);
  const [isTlogExcelUploadModalOpen, setIsTlogExcelUploadModalOpen] = useState(false);
  const [isRenameJobNumberModalOpen, setIsRenameJobNumberModalOpen] = useState(false);
  const pendingRenameJobNumberRef = useRef<{ oldJobNumber: string; newJobNumber: string } | null>(null);
  const [setupData, setSetupData] = useState<any>({});
  const [pipeSizesData, setPipeSizesData] = useState<any[]>([]);
  const [selectedPipe, setSelectedPipe] = useState<any>(null);
  const [unit, setUnit] = useState<string>('');
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const initialLoadRef = useRef<boolean>(false);
  const [LoadProductionData, setLoadProductionData] = useState<boolean>(true);
  const projectSetRef = useRef<boolean>(false);
  // Removed data360ProjectId state - now using actual project prop directly
  const [isWeldDataOpen, setIsWeldDataOpen] = useState(false);
  const [isProductionDataOpen, setIsProductionDataOpen] = useState(true);
  const [filteredWeldIds, setFilteredWeldIds] = useState<string[]>([]);
  const [isWeldParametersModalOpen, setIsWeldParametersModalOpen] = useState(false);
  const [isReleaseNotesModalOpen, setIsReleaseNotesModalOpen] = useState(false);
  const [isReleaseNotesViewModalOpen, setIsReleaseNotesViewModalOpen] = useState(false);
  const [isProductionReportModalOpen, setProductionReportModalOpen] = useState(false);
  const [isProductionReportViewModalOpen, setIsProductionReportViewModalOpen] = useState(false);
  const [unassignSelectedPipe, setUnassignSelectedPipe] = useState<boolean>(false);
  const [isPumpHouseCoatingModalOpen, setIsPumpHouseCoatingModalOpen] = useState(false);
  const previousProjectIdRef = useRef<string | null>(null);
  const previousAssignPipeLoadingRef = useRef<boolean>(false);
  const [lastProjectId, setLastProjectId] = useState<string | number | null>(null);
  const isFetchingPipeDetailsRef = useRef<boolean>(false);
  const retryCountRef = useRef<number>(0);
  const [isPumpHouseView, setIsPumpHouseView] = useState(true);
  const [showProjectDashboard, setShowProjectDashboard] = useState(false);
  const [isNCRRegisterModalOpen, setIsNCRRegisterModalOpen] = useState(false);

  // Add weld data integration
  const { weldData, currentProject, loadWeldData } = useWeldData();

  /** Reload weld data for the pipe currently shown in the selector (including Unassigned). */
  const reloadCurrentWeldData = useCallback(() => {
    if (unassignSelectedPipe) {
      loadWeldData(null, -1 as any);
      return;
    }
    loadWeldData(selectedPipe?.jobNumber?.jobNumbers || null, selectedPipe?.pipeId || null);
  }, [loadWeldData, selectedPipe?.jobNumber?.jobNumbers, selectedPipe?.pipeId, unassignSelectedPipe]);

  const handleOpenWeldParametersFromDownload = useCallback(() => {
    setIsDeviceManagementModalOpen(false);
    setIsWeldParametersModalOpen(true);
  }, []);

  const handleOpenPqrReport = useCallback(() => {
    const allWeldNumbers = (Array.isArray(weldData) ? weldData : [])
      .map((weld: any) => weld?.weldNumber?.toString?.() ?? String(weld?.weldNumber ?? ''))
      .filter((weldNumber: string) => Boolean(weldNumber));

    // De-dupe while preserving order
    const uniqueWeldNumbers: string[] = [];
    const seen = new Set<string>();
    for (const w of allWeldNumbers) {
      if (!seen.has(w)) {
        seen.add(w);
        uniqueWeldNumbers.push(w);
      }
    }

    setPqrWeldNumbers(uniqueWeldNumbers);
    setIsPqrReportModalOpen(true);
  }, [weldData]);

  // Get assembly data for Release Notes modal
  const { assemblyData } = useProductionData();

  // Get assign pipe loading state from Redux
  const assignPipeLoading = useSelector((state: any) => state.weldData?.assignPipeLoading || false);
  const assignPipeSuccess = useSelector((state: any) => state.weldData?.assignPipeSuccess || false);
  const pendingAssignPipeRef = useRef<{
    weldNumbers: string[];
    jobNumbers: string[];
    pipeId?: number | string | null;
    pipeName?: string | null;
  } | null>(null);
  const renameJobNumberLoading = useSelector((state: any) => state.weldData?.renameJobNumberLoading || false);
  const renameJobNumberSuccess = useSelector((state: any) => state.weldData?.renameJobNumberSuccess || false);

  // Get pipeDetails from Redux store
  const { pipeDetails, isPipeDetailsLoading } = useSelector((state: any) => state.setupData || {});

  // Removed projects loading - no longer needed

  // Use Redux hook for limits data and status calculation method
  const { statusCalculationMethod, loadLimitsData } = useLimitsData();

  const availableJobNumbers = useMemo(() => {
    const fromPipe = selectedPipe?.jobNumber?.jobNumbers || [];
    const fromWelds = getDistinctJobNumbersFromWelds(Array.isArray(weldData) ? weldData : []);
    return mergePipeJobNumbers(fromPipe, fromWelds);
  }, [selectedPipe, weldData]);

  const handleRenameJobNumber = useCallback((oldJobNumber: string, newJobNumber: string) => {
    const projectId = project?.project?.projectId;
    if (!projectId || renameJobNumberLoading) {
      return;
    }

    pendingRenameJobNumberRef.current = { oldJobNumber, newJobNumber };
    dispatch(renameJobNumber({
      project_id: parseInt(String(projectId), 10),
      old_job_number: oldJobNumber,
      new_job_number: newJobNumber,
    }));
  }, [dispatch, project?.project?.projectId, renameJobNumberLoading]);

  useEffect(() => {
    if (!renameJobNumberSuccess || !pendingRenameJobNumberRef.current) {
      return;
    }

    const { oldJobNumber, newJobNumber } = pendingRenameJobNumberRef.current;
    pendingRenameJobNumberRef.current = null;
    setIsRenameJobNumberModalOpen(false);

    logJobNumberRenamed({
      projectId: project?.project?.projectId,
      projectName: project?.project?.projectName,
      pipeId: selectedPipe?.pipeId ?? null,
      pipeName: selectedPipe?.pipeName ?? selectedPipe?.pipeSize ?? null,
      oldJobNumber,
      newJobNumber,
    });

    const updatePipeJobNumbers = async () => {
      const existingJobNumbers = selectedPipe?.jobNumber?.jobNumbers || [];
      const updatedJobNumbers = existingJobNumbers.map((jobNumber: string) =>
        jobNumber.toLowerCase() === oldJobNumber.toLowerCase() ? newJobNumber : jobNumber
      );

      if (selectedPipe?.pipeId && project?.project?.projectId) {
        if (havePipeJobNumbersChanged(existingJobNumbers, updatedJobNumbers)) {
          await SaveProjectSetupJobNumbers({
            projectId: project.project.projectId,
            pipeId: selectedPipe.pipeId,
            jobNumber: {
              numberOfJobs: updatedJobNumbers.length,
              jobNumbers: updatedJobNumbers,
            },
          });
        }

        const updatedSelectedPipe = {
          ...selectedPipe,
          jobNumber: {
            ...selectedPipe.jobNumber,
            numberOfJobs: updatedJobNumbers.length,
            jobNumbers: updatedJobNumbers,
          },
        };
        setSelectedPipe(updatedSelectedPipe);
        setPipeSizesData((prev) =>
          prev.map((pipe) =>
            pipe.pipeId === selectedPipe.pipeId
              ? {
                  ...pipe,
                  jobNumber: {
                    ...pipe.jobNumber,
                    numberOfJobs: updatedJobNumbers.length,
                    jobNumbers: updatedJobNumbers,
                  },
                }
              : pipe
          )
        );
      }

      await new Promise((resolve) => setTimeout(resolve, 500));

      if (unassignSelectedPipe) {
        loadWeldData(null, -1 as any);
      } else {
        loadWeldData(updatedJobNumbers.length > 0 ? updatedJobNumbers : null, selectedPipe?.pipeId || null);
      }

      if (project?.project?.projectId) {
        loadLimitsData();
        dispatch(getAllPipeDetails({ projectId: project.project.projectId }));
      }
    };

    updatePipeJobNumbers();
  }, [renameJobNumberSuccess, selectedPipe, project?.project?.projectId, dispatch, loadWeldData, loadLimitsData, unassignSelectedPipe]);

  // Removed handler for data360ProjectId changes - now using actual project prop directly

  // Fetch status calculation method from limits API using Redux saga
  // Uses centralized project manager - no parameters needed
  useEffect(() => {
    if (project.project && project.project.projectId) {
      loadLimitsData(); // No parameters needed - uses centralized project data
    }
  }, [project.project?.projectId]);


  // Load initial data - only fetch if project ID changed or pipeDetails doesn't match current project
  useEffect(() => {
    const currentProjectId = project.project.projectId;

    // Skip if no project ID or already fetching
    if (!currentProjectId || isPipeDetailsLoading || isFetchingPipeDetailsRef.current) {
      return;
    }

    // Check if project ID changed
    const projectIdChanged = lastProjectId !== null && lastProjectId !== currentProjectId;

    // Check if pipeDetails exists and matches current project ID
    const pipeDetailsMatchesProject = pipeDetails && Object.keys(pipeDetails).length > 0 &&
      Object.values(pipeDetails).some((setup: any) =>
        setup.projectId && String(setup.projectId) === String(currentProjectId)
      );

    // Only fetch pipeDetails if:
    // 1. Project ID changed (need to fetch new project's data)
    // 2. pipeDetails is null/undefined/empty
    // 3. pipeDetails exists but doesn't match current project ID
    if (currentProjectId) {
      if (projectIdChanged || !pipeDetails || Object.keys(pipeDetails).length === 0 || !pipeDetailsMatchesProject) {
        isFetchingPipeDetailsRef.current = true;
        dispatch(getAllPipeDetails({ projectId: currentProjectId }));
        // dispatch(getListMaterialCategory({}));
      }
    }

    // Update last project ID
    if (currentProjectId) {
      setLastProjectId(currentProjectId);
    }
  }, [project.project.projectId]);

  // Get current project for weld data functionality
  const { currentProject: currentProjectFromRedux, setProject } = useCurrentProject();

  // // Debug logging
  // useEffect(() => {
  //   // Projects state logged
  // }, [projects, projectsLoading, projectsError]);

  useEffect(() => {
    if (assignPipeSuccess && pendingAssignPipeRef.current) {
      const pending = pendingAssignPipeRef.current;
      pendingAssignPipeRef.current = null;
      logPipeAssignedToWeld({
        projectId: project?.project?.projectId,
        projectName: project?.project?.projectName,
        pipeId: pending.pipeId,
        pipeName: pending.pipeName,
        weldNumbers: pending.weldNumbers,
        jobNumbers: pending.jobNumbers,
      });
    }
  }, [assignPipeSuccess, project?.project?.projectId, project?.project?.projectName]);

  useEffect(() => {
    // Clear selections when assignment completes (assignPipeLoading goes from true to false)
    if (previousAssignPipeLoadingRef.current && !assignPipeLoading) {
      if (!assignPipeSuccess) {
        pendingAssignPipeRef.current = null;
      }
      // Assignment just completed, clear all selections and reload weld data
      setSelectedWeldNumbersForReport([]);
      setSelectedWeldAndJobNumbersForReport([]);
      setSelectedPipeForAssignment(null);
      setFilteredWeldIdsForReport([]);
      // Reload weld data to reflect the updated assignments
      setTimeout(() => {
        reloadCurrentWeldData();
      }, 100);
    }

    // Update the previous value for next comparison
    previousAssignPipeLoadingRef.current = assignPipeLoading;
  }, [assignPipeLoading, assignPipeSuccess, reloadCurrentWeldData]);

  // Set current project for weld data functionality
  useEffect(() => {
    // console.log('🔄 ProjectProduction - useEffect', project);
    if (project && project.project && !projectSetRef.current) {
      // Setting current project
      setProject(project);
      projectSetRef.current = true;
    }
  }, [project, setProject]);

  // Effect to open assembly modal when selectedAssembly changes
  useEffect(() => {

    if (selectedItem) {
      setSelectedAssembly(selectedItem.assemblyNumber);
      setIsAssemblyModalOpen(true);
      setLoadProductionData(false);
    }
  }, [selectedItem]);

  // Graph View state
  const [selectedGraphTypes, setSelectedGraphTypes] = useState([
    'Travel Speed (in/min)',
    'Volt (V)',
    'Current (A)',
    'Wire Speed (in/min)',
    'Oscillation Width (in)',
    'Target (A)'
  ]);

  // Dropdown options (color, label)
  const graphOptions = [
    { value: 'Travel Speed (in/min)', label: 'Travel Speed (in/min)', color: '#EB1C37' },
    { value: 'Volt (V)', label: 'Volt (V)', color: '#FFA500' },
    { value: 'Current (A)', label: 'Current (A)', color: '#ee82ee' },
    { value: 'Wire Speed (in/min)', label: 'Wire Speed (in/min)', color: '#800080' },
    { value: 'Oscillation Width (in)', label: 'Oscillation Width (in)', color: '#A52A2A' },
    { value: 'Target (A)', label: 'Target (A)', color: '#808080' },
    { value: 'Horizontal Bias (A)', label: 'Horizontal Bias (A)', color: '#00FFFF' },
    { value: 'Frequency (cyc/min)', label: 'Frequency (cyc/min)', color: '#FFC0CB' },
  ];

  // Default/mock data for each metric
  const defaultGraphData: Record<
    'Travel Speed (in/min)' | 'Volt (V)' | 'Current (A)' | 'Wire Speed (in/min)' | 'Oscillation Width (in)' | 'Target (A)' | 'Horizontal Bias (A)' | 'Frequency (cyc/min)',
    number[]
  > = {
    'Travel Speed (in/min)': [15, 18, 20, 22, 21, 19, 23, 25, 24, 22, 21, 20],
    'Volt (V)': [20, 21, 21.5, 22, 21.8, 21.6, 21.7, 21.9, 22, 21.8, 21.7, 21.6],
    'Current (A)': [180, 185, 190, 188, 187, 186, 189, 191, 190, 188, 187, 186],
    'Wire Speed (in/min)': [120, 122, 125, 127, 126, 124, 123, 125, 126, 127, 125, 124],
    'Oscillation Width (in)': [0.5, 0.6, 0.7, 0.65, 0.68, 0.7, 0.72, 0.7, 0.68, 0.65, 0.6, 0.5],
    'Target (A)': [185, 185, 185, 185, 185, 185, 185, 185, 185, 185, 185, 185],
    'Horizontal Bias (A)': [0, 1, 0, -1, 0, 1, 0, -1, 0, 1, 0, -1],
    'Frequency (cyc/min)': [60, 62, 61, 63, 62, 61, 60, 62, 61, 63, 62, 61],
  };
  const graphCategories = Array.from({ length: 12 }, (_, i) => (i * 7 + 2).toString());

  useEffect(() => {
    if (project.project.unit) {
      setUnit(project.project.unit || 'in');
    }
    setIsManualWelding(projectHasManualWeldingEnabled(project.project.projectTypes));
  }, [project.project.unit, project.project.projectTypes]);

  const getCoating = (type: string, subType: string) => {
    if (type === 'fabrication' && subType === 'buckleArrestor') {
      return project.project.projectTypes.fabrication?.options?.buckleArrestor?.features?.coating?.enabled;
    }
    else if (type === 'fabrication' && subType === 'pipeInPipe') {
      return project.project.projectTypes.fabrication?.options?.pipeInPipe?.features?.coating?.enabled;
    }
    else if (type === 'fabrication' && subType === 'bulkhead') {
      return project.project.projectTypes.fabrication?.options?.bulkhead?.features?.coating?.enabled;
    }
    else if (type === 'pipeline' && subType === 'none') {
      return project.project.projectTypes.pipeline?.features?.coating?.enabled;
    }
    else if (type === 'others' && subType === 'none') {
      return project.project.projectTypes.others?.features?.coating?.enabled;
    }

    return false;
  }

  const getPumpHouseEnabled = (type: string, subType: string) => {
    if (type === 'pipeline' && subType === 'none') {
      return !!(project.project.projectTypes.pipeline?.features?.coating?.machines as any)?.pumpHouse?.enabled;
    }
    return false;
  }

  const isEnabledWelding = (type: string, subType: string) => {
    if (type === 'fabrication' && subType === 'buckleArrestor') {
      return project.project.projectTypes.fabrication?.options?.buckleArrestor?.features?.welding?.enabled;
    }
    else if (type === 'fabrication' && subType === 'pipeInPipe') {
      return project.project.projectTypes.fabrication?.options?.pipeInPipe?.features?.welding?.enabled;
    }
    else if (type === 'fabrication' && subType === 'bulkhead') {
      return project.project.projectTypes.fabrication?.options?.bulkhead?.features?.welding?.enabled;
    }
    else if (type === 'pipeline' && subType === 'none') {
      return project.project.projectTypes.pipeline?.features?.welding?.enabled;
    }
    else if (type === 'others' && subType === 'none') {
      return project.project.projectTypes.others?.features?.welding?.enabled;
    }

    return false;
  }

  const isEnabledNDT = (type: string, subType: string) => {
    if (type === 'fabrication' && subType === 'buckleArrestor') {
      return project.project.projectTypes.fabrication?.options?.buckleArrestor?.features?.ndt?.enabled;
    }
    else if (type === 'fabrication' && subType === 'pipeInPipe') {
      return project.project.projectTypes.fabrication?.options?.pipeInPipe?.features?.ndt?.enabled;
    }
    else if (type === 'fabrication' && subType === 'bulkhead') {
      return project.project.projectTypes.fabrication?.options?.bulkhead?.features?.ndt?.enabled;
    }
    else if (type === 'pipeline' && subType === 'none') {
      return project.project.projectTypes.pipeline?.features?.ndt?.enabled;
    }
    else if (type === 'others' && subType === 'none') {
      return project.project.projectTypes.others?.features?.ndt?.enabled;
    }

    return false;
  }

  const isEnabledHydro = (type: string, subType: string) => {
    if (type !== 'fabrication') return false;
    if (subType === 'buckleArrestor') {
      return !!project.project.projectTypes.fabrication?.options?.buckleArrestor?.features?.hydro?.enabled;
    }
    if (subType === 'pipeInPipe') {
      return !!project.project.projectTypes.fabrication?.options?.pipeInPipe?.features?.hydro?.enabled;
    }
    if (subType === 'bulkhead') {
      return !!project.project.projectTypes.fabrication?.options?.bulkhead?.features?.hydro?.enabled;
    }
    if (subType === 'spools') {
      return !!project.project.projectTypes.fabrication?.options?.spools?.features?.hydro?.enabled;
    }
    return false;
  }

  const isEnabledTorquing = (type: string, subType: string) => {
    if (type !== 'fabrication') return false;
    if (subType === 'buckleArrestor') {
      return !!project.project.projectTypes.fabrication?.options?.buckleArrestor?.features?.torquing?.enabled;
    }
    if (subType === 'pipeInPipe') {
      return !!project.project.projectTypes.fabrication?.options?.pipeInPipe?.features?.torquing?.enabled;
    }
    if (subType === 'bulkhead') {
      return !!project.project.projectTypes.fabrication?.options?.bulkhead?.features?.torquing?.enabled;
    }
    if (subType === 'spools') {
      return !!project.project.projectTypes.fabrication?.options?.spools?.features?.torquing?.enabled;
    }
    return false;
  }

  // Function to extract pipe sizes from setup data
  // Function to extract pipe sizes from setup data
  const extractPipeSizes = (setupData: any): any[] => {
    const pipeSizes: any[] = [];
    Object.values(setupData).forEach((setup: any) => {
      const coating = getCoating(setup.projectType, setup.projectSubType);
      const isWeldingEnabled = isEnabledWelding(setup.projectType, setup.projectSubType);
      const NDT = isEnabledNDT(setup.projectType, setup.projectSubType);
      const Hydro = isEnabledHydro(setup.projectType, setup.projectSubType);
      const Torquing = isEnabledTorquing(setup.projectType, setup.projectSubType);
      const isPumpHouseEnabled = getPumpHouseEnabled(setup.projectType, setup.projectSubType);


      if (setup.pipeSizes && Array.isArray(setup.pipeSizes)) {
        setup.pipeSizes.forEach((pipe: any) => {
          const pipeData = {
            pipeId: pipe.pipeId,
            pipeName: pipe.pipeName,
            projectSubType: setup.projectSubType,
            projectType: setup.projectType,
            projectId: setup.projectId,
            projectTypeId: setup.projectTypeId,
            size: pipe.pipeSize || pipe.size || 0,
            wallThickness: pipe.wallThickness || 0,
            documentsData: pipe.documentsData,
            jobNumber: pipe.jobNumber,
            prefixAssemblyName: pipe.prefixAssemblyName,
            numberOfBuckleArrestors: pipe.numberOfBuckleArrestors,
            coating: coating,
            isCoatingEnabled: coating,
            isWeldingEnabled: isWeldingEnabled,
            isPumpHouseEnabled: isPumpHouseEnabled,
            NDT: NDT,
            Hydro: Hydro,
            Torquing: Torquing,
            isReducerPipe: pipe.pipeDetailsExtraDetails?.isReducerPipe || false,
            isFlangePipe: pipe.pipeDetailsExtraDetails?.isFlangePipe || false,
            displayName: setup.projectSubType === 'none' && setup.projectType !== 'fabrication' ?
              `${setup.projectType === 'pipeline' ? 'Pipeline' : setup.projectType === 'others' ? 'Others' : setup.projectType} · ${pipe.pipeName} (${pipe.pipeSize}" × ${pipe.wallThickness}")` :
              `${setup.projectSubType === 'buckleArrestor' ? 'Buckle Arrestor' :
                setup.projectSubType === 'pipeInPipe' ? 'Pipe in Pipe' :
                  setup.projectSubType === 'bulkhead' ? 'Bulk Head' :
                    setup.projectSubType === 'spools' ? 'Spools' :
                      setup.projectSubType} · ${pipe.pipeName} (${pipe.pipeSize}" × ${pipe.wallThickness}")`
          };

          // Extracted pipe data
          pipeSizes.push(pipeData);
        });
      }
    });

    return pipeSizes;
  };



  // Update pipe sizes when pipeDetails changes from Redux.
  // Preserve the currently selected pipe by id so opening an assembly (or list refresh)
  // does not jump the Pipe Size dropdown back to the first pipe.
  useEffect(() => {
    if (pipeDetails && Object.keys(pipeDetails).length > 0) {
      // Reset fetching flag when data is received
      isFetchingPipeDetailsRef.current = false;
      retryCountRef.current = 0;

      // Extract pipe sizes
      const extractedPipeSizes = extractPipeSizes(pipeDetails);
      setPipeSizesData(extractedPipeSizes);
      if (extractedPipeSizes && extractedPipeSizes.length > 0) {
        setSelectedPipe((prev: any) => {
          if (prev?.pipeId != null) {
            const match = extractedPipeSizes.find(
              (p: any) => String(p.pipeId) === String(prev.pipeId)
            );
            if (match) {
              const idx = extractedPipeSizes.findIndex(
                (p: any) => String(p.pipeId) === String(prev.pipeId)
              );
              if (idx >= 0) {
                setSelectedPipeSize((current) => (current === idx ? current : idx));
              }
              return match;
            }
          }
          const safeIndex = Math.min(
            Math.max(selectedPipeSize, 0),
            extractedPipeSizes.length - 1
          );
          return extractedPipeSizes[safeIndex];
        });
        setDataLoaded(true);
      } else {
        setPipeSizesData([]);
      }
    }
    // Intentionally depend only on pipeDetails — selectedPipeSize is applied via PipeSelector onChange
    // and preserved here by pipeId so assembly actions do not reset to the first pipe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipeDetails]);



  // Update loading state from Redux
  useEffect(() => {
    setIsLoadingData(isPipeDetailsLoading);
  }, [isPipeDetailsLoading]);

  const refreshMaterials = async () => {
    try {
      if (selectedPipe) {
        const payload = {
          project_id: selectedPipe.projectId,
          project_type_id: selectedPipe.projectTypeId,
          pipe_id: selectedPipe.pipeId
        }
        const materialsData = await GetAllMaterials(payload);
        if (materialsData) {
          //  Setting materials data
          setSetupData(materialsData);
        }
        setLoadProductionData(false);
      }
    } catch (error) {
    }
  }

  useEffect(() => {
    refreshMaterials();
  }, [selectedPipe]);

  useEffect(() => {
    if (LoadProductionData) {
      refreshMaterials();
    }
  }, [LoadProductionData]);

  // Load weld data when component mounts or project changes - WITH CLEANUP
  useEffect(() => {

    const currentProjectId = project?.project?.projectId;

    if (currentProjectId && selectedPipe) {
      // Check if project has actually changed
      const hasProjectChanged = previousProjectIdRef.current && previousProjectIdRef.current !== currentProjectId;

      if (hasProjectChanged) {
        // console.log('🔄 ProjectProduction - Project changed, clearing all weld data');
        // Clear ALL weld data (main weld data + all view data) when switching projects
        dispatch(clearWeldData());
        dispatch(clearAllWeldViews());
      }

      // Set the current project in the weld data store and load fresh data
      if (currentProjectFromRedux?.project?.projectId !== currentProjectId || hasProjectChanged) {
        // console.log('🔄 ProjectProduction - Loading fresh weld data for project:', currentProjectId);
        // Small delay to ensure Redux state is cleared
        const loadTimer = setTimeout(() => {
          reloadCurrentWeldData();
        }, hasProjectChanged ? 200 : 0);

        // Update the ref
        previousProjectIdRef.current = currentProjectId;

        return () => clearTimeout(loadTimer);
      }
    }
  }, [project?.project?.projectId, currentProjectFromRedux?.project?.projectId, dispatch, reloadCurrentWeldData, selectedPipe]);

  // Note: Pipe selection reload is handled directly in onValueChange handler
  // // This useEffect remains as a fallback for any other selectedPipe changes
  // useEffect(() => {
  //   if (selectedPipe && selectedPipe.isWeldingEnabled && (selectedPipe.projectType === 'pipeline' || !isProductionDataOpen)) {
  //     // Only reload if we have a valid project and pipe with welding enabled
  //     const currentProjectId = project?.project?.projectId;
  //     if (currentProjectId && currentProjectFromRedux?.project?.projectId === currentProjectId) {
  //       // Only reload if jobNumber actually changed (to avoid duplicate calls from onValueChange)
  //       // The direct call in onValueChange handles immediate user interaction
  //       // This useEffect serves as a fallback for programmatic selectedPipe changes
  //       console.log('🔄 ProjectProduction - Selected pipe changed (fallback), reloading weld data with jobNumber:', selectedPipe?.jobNumber);
  //       // Use a longer delay here to let onValueChange handler run first if both trigger
  //       const loadTimer = setTimeout(() => {
  //         loadWeldData(selectedPipe?.jobNumber?.jobNumbers || null, selectedPipe?.pipeId || null);
  //       }, 300);

  //       return () => clearTimeout(loadTimer);
  //     }
  //   }
  // }, [selectedPipe?.jobNumber, selectedPipe?.isWeldingEnabled, selectedPipe?.projectType, isProductionDataOpen, project?.project?.projectId, currentProjectFromRedux?.project?.projectId, dispatch, loadWeldData]);

  // State for filtered weld IDs from the main table for report generation
  const [filteredWeldIdsForReport, setFilteredWeldIdsForReport] = useState<string[]>([]);
  // State for selected weld numbers from checkboxes
  const [selectedWeldNumbersForReport, setSelectedWeldNumbersForReport] = useState<string[]>([]);
  // State for selected pipe for assignment
  const [selectedPipeForAssignment, setSelectedPipeForAssignment] = useState<string | null>(null);
  // State for selected weld and job numbers for assignment
  const [selectedWeldAndJobNumbersForReport, setSelectedWeldAndJobNumbersForReport] = useState<string[]>([]);

  const assignablePipeOptions = useMemo(() => {
    if (!pipeSizesData?.length) return [];

    const sourcePipeId = !unassignSelectedPipe && selectedPipe?.pipeId ? selectedPipe.pipeId : null;

    return pipeSizesData
      .map((pipe, index) => ({ pipe, index }))
      .filter(({ pipe }) => !sourcePipeId || pipe.pipeId !== sourcePipeId)
      .map(({ pipe, index }) => ({ id: index, name: pipe.displayName ?? '' }));
  }, [pipeSizesData, unassignSelectedPipe, selectedPipe?.pipeId]);

  useEffect(() => {
    if (!selectedPipeForAssignment) return;

    const isStillAssignable = assignablePipeOptions.some(
      (option) => String(option.id) === selectedPipeForAssignment
    );

    if (!isStillAssignable) {
      setSelectedPipeForAssignment(null);
    }
  }, [assignablePipeOptions, selectedPipeForAssignment]);

  // Memoize the callback to prevent unnecessary re-renders
  const handleFilteredDataChange = useCallback((filteredWeldIds: string[], selectedWeldNumbers?: string[], selectedWeldAndJobNumbers?: string[]) => {
    //  console.log('🔄 ProjectProduction - handleFilteredDataChange', selectedWeldNumbers, selectedWeldAndJobNumbers);
    setFilteredWeldIdsForReport(filteredWeldIds);
    setSelectedWeldNumbersForReport(selectedWeldNumbers || []);
    setSelectedWeldAndJobNumbersForReport(selectedWeldAndJobNumbers || []);
  }, []);

  // Get filtered weld IDs for report generation (keeping parameter name as weldNumbers)
  // Priority: selected welds > filtered welds > all welds
  // Format: "weldNumber:wpsNumber" combination string array
  const getFilteredWpsNumbers = useMemo(() => {
    //console.log('🔄 ProjectProduction - getFilteredWpsNumbers', selectedWeldNumbersForReport);

    // If user selected specific welds via checkboxes, use those
    if (selectedWeldNumbersForReport.length > 0) {
      return selectedWeldAndJobNumbersForReport;
    }

    return selectedWeldAndJobNumbersForReport;
  }, [selectedWeldNumbersForReport, selectedWeldAndJobNumbersForReport]);



  // Notify parent when view mode changes
  useEffect(() => {
    if (onViewModeChange) {
      onViewModeChange(viewMode);
    }
  }, [viewMode, onViewModeChange]);

  // Center map when switching to map view
  useEffect(() => {
    if (viewMode === 'map' && selectedPipeSize !== undefined) {
      // Small delay to ensure map is rendered
      setTimeout(() => {
        const mapElement = document.querySelector('.map-container');
        if (mapElement) {
          // Trigger a resize event to ensure map renders properly
          window.dispatchEvent(new Event('resize'));
        }
      }, 100);
    }
  }, [viewMode, selectedPipeSize]);

  // Handler for dropdown change
  const handleGraphTypeChange = (index: number, value: string) => {
    setSelectedGraphTypes((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleNDTUpload = async (assemblyNumber: string, file: File) => {
    const newNDTData: NDTData = {
      id: `ndt-${Date.now()}`,
      assemblyNumber,
      file,
      uploadDate: new Date(Date.now()).toISOString().split('T')[0],
      status: 'pending'
    };
    setNDTData([...ndtData, newNDTData]);
  };

  const handleOpenWeldData = (assembly: any, weldIds?: string[]) => {
    //  console.log('handleOpenWeldData', assemblyNumber, weldIds);
    setIsWeldDataOpen(true);
    setSelectedAssemblyForWeld(assembly.assemblyNumber);
    setSelectedAssemblyForWeldData(assembly);
    // Store weld IDs for filtering
    if (weldIds) {
      setFilteredWeldIds(weldIds);
    }
    // setIsWeldDataModalOpen(true);
  };

  const handleCloseWeldData = () => {
    setSelectedAssemblyForWeld(null);
    setSelectedAssemblyForWeldData(null);
    // setIsWeldDataModalOpen(false);
    setIsWeldDataOpen(false);
    setFilteredWeldIds([]);
  };


  // Check if there are any specifications for this subtype
  const hasAnyPipes = !isLoadingData && pipeSizesData.length > 0;
  const isMapView = viewMode === 'map';
  // Check if there are any specifications for this subtype
  //If no pipes exist for this subtype, show message to create pipes first
  if (!hasAnyPipes && !isLoadingData) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 text-purple-800">

          <h3 className="text-xl font-semibold">Production</h3>
        </div>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 bg-page-bg rounded-full flex items-center justify-center mb-4">
            <Cable className="h-8 w-8 text-heritage-grey" />
          </div>
          <h3 className="text-lg font-medium text-blueprint-blue mb-2">No pipes created yet</h3>
          <p className="text-heritage-grey max-w-sm">
            You need to create pipes in the Setup section first before adding specifications. Go to Setup → Pipeline to configure your pipes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {isLoadingData && pipeSizesData.length === 0 ? (
        <div className="flex items-center justify-center min-h-[700px]">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hero-red"></div>
            <span className="text-heritage-grey text-lg">Loading production data...</span>
          </div>
        </div>
      ) : (
        <>

          <div>
            {/* Page context; actions live in the Pipe Size toolbar below. */}
            {/* <div className="mb-4 px-6">
              <p className="text-[12px] text-heritage-grey">
                {selectedPipe === null ? 'Select a pipe size' :
                  selectedPipe.projectType === 'pipeline' ? 'Pipeline' :
                    selectedPipe.projectType === 'fabrication' ?
                      (selectedPipe.projectSubType === 'buckleArrestor' ? 'Buckle Arrestor Fabrication' :
                        selectedPipe.projectSubType === 'pipeInPipe' ? 'Pipe in Pipe Fabrication' :
                          selectedPipe.projectSubType === 'bulkhead' ? 'Bulk Head Fabrication' :
                            selectedPipe.projectSubType) : 'Others'}
              </p>
            </div> */}

            {/* Table View */}
            {viewMode === 'table' && (
              <Card className="border-none shadow-none">
                <CardContent className="flex min-h-[calc(100vh-340px)] flex-col">
                  <>
                    {/* Pipe Size Selection and Switch */}
                    <div className="mb-6 flex-shrink-0">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
                          <Label className="shrink-0 whitespace-nowrap text-[12px] font-medium text-text-grey sm:mr-1">Pipe Size:</Label>
                          <PipeSelector
                            triggerWidth="w-full sm:w-[400px]"
                            value={
                              unassignSelectedPipe
                                ? 'unassign'
                                : (pipeSizesData?.[selectedPipeSize]?.displayName ?? null)
                            }
                            onChange={(selectedValue: string) => {
                              if (selectedValue === 'unassign') {
                                // Handle unassign - load weld data with pipe_id = -1
                                setSelectedWeldNumbersForReport([]);
                                setSelectedWeldAndJobNumbersForReport([]);
                                setSelectedPipeForAssignment(null);
                                setFilteredWeldIdsForReport([]);
                                setUnassignSelectedPipe(true);
                                setIsProductionDataOpen(false);
                                setCurrentPage(1);
                                dispatch(clearWeldData());
                                dispatch(clearAllWeldViews());

                                setTimeout(() => {
                                  loadWeldData(null, -1 as any);
                                }, 100);
                                return;
                              }

                              const index = pipeSizesData?.findIndex((p) => p.displayName === selectedValue) ?? -1;
                              if (index < 0) {
                                return;
                              }
                              // Clear all checkbox selections when pipe size changes
                              setSelectedWeldNumbersForReport([]);
                              setSelectedWeldAndJobNumbersForReport([]);
                              setSelectedPipeForAssignment(null);
                              setFilteredWeldIdsForReport([]);
                              setSelectedPipeSize(index);
                              setCurrentPage(1);
                              setUnassignSelectedPipe(false);
                              const newSelectedPipe = pipeSizesData?.[index];
                              if (!newSelectedPipe) {
                                return;
                              }
                              setSelectedPipe(newSelectedPipe);
                              if (newSelectedPipe.projectType === 'pipeline') {
                                setIsProductionDataOpen(false);
                                if (newSelectedPipe.isWeldingEnabled) {
                                  dispatch(clearWeldData());
                                  dispatch(clearAllWeldViews());
                                  setTimeout(() => {
                                    loadWeldData(newSelectedPipe?.jobNumber?.jobNumbers || null, newSelectedPipe?.pipeId || null);
                                  }, 100);
                                }
                              } else if (newSelectedPipe.projectType === 'fabrication') {
                                setIsProductionDataOpen(true);
                                setLoadProductionData(false);
                                setTimeout(() => {
                                  setLoadProductionData(true);
                                }, 50);
                              } else {
                                setIsProductionDataOpen(true);
                              }
                            }}
                            placeholder="Select pipe size"
                            searchable
                            searchPlaceholder="Search pipe size..."
                            options={[
                              ...((pipeSizesData || []).map((pipe) => ({
                                value: pipe.displayName,
                                label: pipe.displayName,
                              }))),
                              ...((pipeSizesData.length > 0 && !isCustomerRepresentativeUser)
                                ? [{ value: 'unassign', label: 'Unassigned Welds (All Pipes)' }]
                                : []),
                            ]}
                          />
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          {selectedPipe && selectedPipe.projectType === 'pipeline' && selectedPipe.isPumpHouseEnabled && selectedPipe.isWeldingEnabled && (
                            <div className="inline-flex bg-page-bg border border-[#E2E5EA] rounded-lg p-[3px]">
                              <button
                                type="button"
                                onClick={() => setIsPumpHouseView(true)}
                                className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all duration-150 ${isPumpHouseView
                                    ? 'bg-white text-blueprint-blue shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
                                    : 'text-heritage-grey hover:text-blueprint-blue'
                                  }`}
                              >
                                Coating Data
                              </button>
                              <button
                                type="button"
                                onClick={() => setIsPumpHouseView(false)}
                                className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all duration-150 ${!isPumpHouseView
                                    ? 'bg-white text-blueprint-blue shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
                                    : 'text-heritage-grey hover:text-blueprint-blue'
                                  }`}
                              >
                                Weld Data
                              </button>
                              

                            </div>
                          )}

                          {/* Production Data / Weld Data toggle — fabrication only */}
                          {selectedPipe && selectedPipe.projectType === 'fabrication' && selectedPipe.isWeldingEnabled && (
                            <div className="inline-flex bg-page-bg border border-[#E2E5EA] rounded-lg p-[3px]">
                              <button
                                type="button"
                                onClick={() => setIsProductionDataOpen(true)}
                                className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all duration-150 flex items-center gap-1.5 ${isProductionDataOpen
                                    ? 'bg-white text-blueprint-blue shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
                                    : 'text-heritage-grey hover:text-blueprint-blue'
                                  }`}
                              >
                                <Package className="h-3.5 w-3.5" />
                                Assemblies
                              </button>
                              <button
                                type="button"
                                onClick={() => setIsProductionDataOpen(false)}
                                className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all duration-150 flex items-center gap-1.5 ${!isProductionDataOpen
                                    ? 'bg-white text-blueprint-blue shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
                                    : 'text-heritage-grey hover:text-blueprint-blue'
                                  }`}
                              >
                                <Zap className="h-3.5 w-3.5" />
                                Welds
                              </button>
                            </div>
                          )}

                          {selectedPipe?.projectSubType === 'spools' && (
                            <>
                              <Button
                                onClick={() => setIsNCRRegisterModalOpen(true)}
                                size="sm"
                                variant="outline"
                                className="flex items-center gap-2"
                              >
                                <FileText className="h-4 w-4" />
                                NCR Register
                              </Button>
                              <div className="inline-flex bg-page-bg border border-[#E2E5EA] rounded-lg p-[3px]">
                                <button
                                  type="button"
                                  onClick={() => setShowProjectDashboard(true)}
                                  className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all duration-150 ${showProjectDashboard
                                      ? 'bg-white text-blueprint-blue shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
                                      : 'text-heritage-grey hover:text-blueprint-blue'
                                    }`}
                                >
                                  Dashboard
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setShowProjectDashboard(false)}
                                  className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all duration-150 ${!showProjectDashboard
                                      ? 'bg-white text-blueprint-blue shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
                                      : 'text-heritage-grey hover:text-blueprint-blue'
                                    }`}
                                >
                                  Spools
                                </button>
                              </div>
                            </>
                          )}

                          {selectedPipe && selectedPipe.projectType === 'pipeline' && selectedPipe.isPumpHouseEnabled && !selectedPipe.isWeldingEnabled && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setIsPumpHouseCoatingModalOpen(true)}
                              className="flex items-center gap-2"
                            >
                              <Upload className="h-4 w-4" />
                              Upload Pump House Data
                            </Button>
                          )}

                          {selectedPipe && selectedPipe.isWeldingEnabled && selectedPipe.projectSubType !== 'spools' && (() => {
                            // Table/Map belongs to Weld Data; hide it on Pump House Coating Data.
                            // On the Assemblies view (fabrication + isProductionDataOpen) they're rendered disabled
                            // so the toolbar doesn't shift when toggling Assemblies <-> Welds.
                            const viewControlsDisabled =
                              selectedPipe.projectType === 'fabrication' && isProductionDataOpen;
                            const isPumpHouseCoatingView =
                              selectedPipe.projectType === 'pipeline' && selectedPipe.isPumpHouseEnabled && isPumpHouseView;
                            return (
                              <>
                                {selectedPipe.isWeldingEnabled && !isPumpHouseCoatingView && (
                                  <div
                                    className={`inline-flex bg-page-bg border border-[#E2E5EA] rounded-lg p-[3px] ${viewControlsDisabled ? 'opacity-50' : ''
                                      }`}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => setViewMode('table')}
                                      disabled={viewControlsDisabled}
                                      className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all duration-150 flex items-center gap-1.5 ${viewControlsDisabled ? 'cursor-not-allowed' : ''
                                        } ${viewMode === 'table'
                                          ? 'bg-white text-blueprint-blue shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
                                          : 'text-heritage-grey hover:text-blueprint-blue'
                                        }`}
                                    >
                                      <TableIcon className="h-3.5 w-3.5" />
                                      Table
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setViewMode('map')}
                                      disabled={viewControlsDisabled}
                                      className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all duration-150 flex items-center gap-1.5 ${viewControlsDisabled ? 'cursor-not-allowed' : ''
                                        } ${isMapView
                                          ? 'bg-white text-blueprint-blue shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
                                          : 'text-heritage-grey hover:text-blueprint-blue'
                                        }`}
                                    >
                                      <Map className="h-3.5 w-3.5" />
                                      Map
                                    </button>
                                  </div>
                                )}

                                {/* Overflow menu */}
                                <DropdownMenu>
                                  <DropdownMenuTrigger className="flex items-center justify-center w-8 h-8 rounded-lg border border-[#E2E5EA] bg-white hover:bg-page-bg transition-colors">
                                    <MoreHorizontal className="h-4 w-4 text-text-grey" />
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    {isPumpHouseCoatingView ? (
                                      <DropdownMenuItem onClick={() => setIsPumpHouseCoatingModalOpen(true)}>
                                        <Upload className="h-4 w-4 mr-2" />
                                        Upload Pump House Data
                                      </DropdownMenuItem>
                                    ) : (
                                      <>
                                        {canReport && (
                                          <DropdownMenuItem onClick={() => setIsReportGenerationModalOpen(true)}>
                                            <FileText className="h-4 w-4 mr-2 flex-shrink-0" />
                                            Generate Report
                                            {selectedWeldNumbersForReport.length > 0 && (
                                              <span className="ml-2 text-[10px] text-heritage-grey">
                                                {selectedWeldNumbersForReport.length}
                                              </span>
                                            )}
                                          </DropdownMenuItem>
                                        )}
                                        {canReport && (
                                          <DropdownMenuItem
                                            disabled={!Array.isArray(weldData) || weldData.length === 0}
                                            onClick={() => {
                                              if (Array.isArray(weldData) && weldData.length > 0) {
                                                handleOpenPqrReport();
                                              }
                                            }}
                                          >
                                            <FileText className="h-4 w-4 mr-2 flex-shrink-0" />
                                            PQR Report
                                          </DropdownMenuItem>
                                        )}
                                        {selectedPipe.projectType == 'fabrication' && selectedPipe.projectSubType !== 'spools' && isProductionDataOpen && assemblyData && assemblyData?.length > 0 && (
                                          <>
                                            <DropdownMenuItem onClick={() => setIsReleaseNotesViewModalOpen(true)}>
                                              <FileText className="h-4 w-4 mr-2 flex-shrink-0" />
                                              IRN Reports
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => setIsReleaseNotesModalOpen(true)}>
                                              <FileText className="h-4 w-4 mr-2 flex-shrink-0" />
                                              Generate IRN
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => setIsProductionReportViewModalOpen(true)}>
                                              <FileText className="h-4 w-4 mr-2 flex-shrink-0" />
                                              Production reports
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => setProductionReportModalOpen(true)}>
                                              <FileText className="h-4 w-4 mr-2 flex-shrink-0" />
                                              Generate production report
                                            </DropdownMenuItem>
                                          </>
                                        )}
                                        <DropdownMenuItem onClick={() => setIsStatusConfigurationModalOpen(true)}>
                                          <Monitor className="h-4 w-4 mr-2" />
                                          Status Configuration
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => setIsDeviceManagementModalOpen(true)}>
                                          <Monitor className="h-4 w-4 mr-2" />
                                          Device Management
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => setIsWeldParametersModalOpen(true)}>
                                          <Monitor className="h-4 w-4 mr-2" />
                                          Weld Parameters
                                        </DropdownMenuItem>

                                        <DropdownMenuItem onClick={() => setIsWeldDataUploadModalOpen(true)}>
                                          <Upload className="h-4 w-4 mr-2" />
                                          Upload Weld Data
                                        </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => setIsRenameJobNumberModalOpen(true)}>
                                            <Pencil className="h-4 w-4 mr-2" />
                                            Rename Job Number
                                          </DropdownMenuItem>
                                          
                                        {/* <DropdownMenuItem onClick={() => setIsTlogExcelUploadModalOpen(true)}>
                                          <Upload className="h-4 w-4 mr-2" />
                                          Upload TLOG Data
                                        </DropdownMenuItem> */}
                                        </>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Table View */}
                    {viewMode === 'table' && (
                      <>
                        <div>
                          <div>
                            {/* Pipe Assignment Section - Only show when weld data items are selected */}
                            {selectedWeldNumbersForReport.length > 0 && selectedPipe?.projectSubType !== 'spools' && (
                              <div className="flex items-center gap-2 mb-4">
                                {/* Pipe Selection Dropdown - Only show if pipes are available */}
                                {assignablePipeOptions.length > 0 ? (
                                  <SearchableSelect
                                    value={selectedPipeForAssignment != null && selectedPipeForAssignment !== '' ? (pipeSizesData[parseInt(selectedPipeForAssignment, 10)]?.displayName ?? '') : ''}
                                    onValueChange={(selectedName: string) => {
                                      const option = assignablePipeOptions.find((pipe) => pipe.name === selectedName);
                                      setSelectedPipeForAssignment(option ? String(option.id) : null);
                                    }}
                                    placeholder="Select pipe"
                                    searchPlaceholder="Search pipe..."
                                    options={assignablePipeOptions}
                                    showAddButton={false}
                                    className="w-[400px]"
                                  />
                                ) : null}

                                {/* Assign Pipe Button - Only enabled when pipe is selected and welds are selected */}
                                <Button
                                  className="flex items-center gap-2"
                                  loading={assignPipeLoading}
                                  disabled={selectedWeldNumbersForReport.length === 0 || !selectedPipeForAssignment || selectedPipeForAssignment === 'unassign' || assignPipeLoading}
                                  onClick={async () => {
                                    if (selectedPipeForAssignment && selectedPipeForAssignment !== 'unassign') {
                                      const pipeIndex = parseInt(selectedPipeForAssignment);
                                      const pipeToAssign = pipeSizesData[pipeIndex];

                                      // Extract weld_number and job_number from weldData
                                      const selectedWelds = weldData.filter((weld: any) =>
                                        selectedWeldNumbersForReport.includes(weld.weldNumber)
                                      );

                                      const weldNumbers = selectedWelds.map((weld: any) => weld.weldNumber);
                                      const jobNumbers = getDistinctJobNumbersFromWelds(selectedWelds);

                                      const payload = {
                                        project_id: project.project.projectId,
                                        weld_number: weldNumbers,
                                        job_number: jobNumbers,
                                        pipe_details_id: pipeToAssign?.pipeId
                                      };

                                      pendingAssignPipeRef.current = {
                                        weldNumbers,
                                        jobNumbers,
                                        pipeId: pipeToAssign?.pipeId ?? null,
                                        pipeName: pipeToAssign?.pipeName ?? pipeToAssign?.pipeSize ?? null,
                                      };

                                      await dispatch(assignPipeToWeld(payload));

                                      // if (jobNumbers.length === 0) {
                                      //   return;
                                      // }

                                      // const targetExistingJobNumbers = pipeToAssign?.jobNumber?.jobNumbers || [];
                                      // const updatedTargetJobNumbers = mergePipeJobNumbers(
                                      //   targetExistingJobNumbers,
                                      //   jobNumbers
                                      // );

                                      // if (havePipeJobNumbersChanged(targetExistingJobNumbers, updatedTargetJobNumbers)) {
                                     
                                      //   await SaveProjectSetupJobNumbers({
                                      //     projectId: project.project.projectId,
                                      //     pipeId: pipeToAssign?.pipeId,
                                      //     jobNumber: {
                                      //       numberOfJobs: updatedTargetJobNumbers.length,
                                      //       jobNumbers: updatedTargetJobNumbers
                                      //     }
                                      //   });
                                      // }

                                      // const sourcePipe = selectedPipe ? selectedPipe : null;
                                      // if (
                                      //   sourcePipe?.pipeId &&
                                      //   pipeToAssign?.pipeId &&
                                      //   sourcePipe.pipeId !== pipeToAssign.pipeId
                                      // ) {
                                      //   const sourceExistingJobNumbers = sourcePipe?.jobNumber?.jobNumbers || [];
                                      //   const updatedSourceJobNumbers =await getUpdatedSourcePipeJobNumbers(
                                      //     sourceExistingJobNumbers,
                                      //     selectedWelds,
                                      //     Array.isArray(weldData) ? weldData : []
                                      //   );

                                      //   if (havePipeJobNumbersChanged(sourceExistingJobNumbers, updatedSourceJobNumbers)) {
                                      //     await SaveProjectSetupJobNumbers({
                                      //       projectId: project.project.projectId,
                                      //       pipeId: sourcePipe.pipeId,
                                      //       jobNumber: {
                                      //         numberOfJobs: updatedSourceJobNumbers.length,
                                      //         jobNumbers: updatedSourceJobNumbers
                                      //       }
                                      //     });
                                      //   }
                                      // }
                                    }

                                  }}
                                >
                                  <PlusIcon className="h-4 w-4" />
                                  Assign Pipe
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                        {selectedPipe?.projectSubType === 'spools' ? (
                          showProjectDashboard ? (
                            <ProjectDashboard
                              projectId={project.project.projectId}
                              pipeId={selectedPipe?.pipeId ?? null}
                            />
                          ) : (
                            <SpoolsProduction
                              selectedPipe={selectedPipe}
                              project={project}
                              canEdit={canEdit}
                              canSign={canSign}
                              isCustomerRepresentativeUser={isCustomerRepresentativeUser}
                              isQCInspectorUser={isQCInspectorUser}
                              isWelderUser={isWelderUser}
                              currentUserTeamMemberId={currentUserTeamMemberId}
                              isAdminOrSuperAdmin={isAdminOrSuperAdmin}
                            />
                          )
                        ) : selectedPipe && selectedPipe.isWeldingEnabled && !(selectedPipe.projectType === 'pipeline' && selectedPipe.isPumpHouseEnabled && isPumpHouseView) ? <>
                          {/* Show content only when pipe size is selected */}
                          {selectedPipe && selectedPipe.projectType ? (
                            <>
                              {/* Main Table - Conditional based on pipe size selection */}
                              <Card className="border-none shadow-none">
                                <CardContent className="p-0">
                                  {selectedPipe.projectType == 'pipeline' || !isProductionDataOpen ? (
                                    // Show Weld Data Table when Pipeline is selected (index 0)
                                    <WeldDataMainTable
                                      projectId={parseInt(project.project.projectId)}
                                      projectName={project.project.projectName}
                                      onFilteredDataChange={handleFilteredDataChange}
                                      onGenerateReport={() => {
                                        // console.log('isProductionDataOpen' , isProductionDataOpen);
                                        //console.log('selectedPipe' , selectedPipe);
                                        // For buckle arrestor/pipe with weld data, open welder signature modal
                                        if (selectedPipe &&
                                          weldData && weldData.length > 0) {
                                          setIsReportGenerationModalOpen(true);

                                          // if(selectedPipe.projectSubType === 'buckleArrestor'){
                                          //   setIsWelderSignatureReportModalOpen(true);
                                          // } else {
                                          //   setIsReportGenerationModalOpen(true);
                                          // }
                                        }
                                      }}
                                      selectedPipe={selectedPipe}
                                      unassignSelectedPipe={unassignSelectedPipe}
                                      statusCalculationMethod={statusCalculationMethod}
                                      statusCalculationStartedAt={statusCalculationStartedAt}
                                      canReport={canReport}
                                      canEdit={canEdit}
                                      isAdminOrSuperAdmin={isAdminOrSuperAdmin}
                                    />
                                  ) : (
                                    // Show Assembly Table for other project types

                                    <>
                                      <AssemblyTable
                                        selectedPipeSize={selectedPipeSize}
                                        project={project.project}
                                        ndtData={ndtData}
                                        handleOpenWeldData={handleOpenWeldData}
                                        handleNDTUpload={handleNDTUpload}
                                        setSelectedAssembly={setSelectedAssembly}
                                        selectedPipe={selectedPipe}
                                        canEdit={canEdit}
                                        canReport={canReport}
                                        canSign={canSign}
                                        materials={setupData}
                                        selectedItem={selectedItem}
                                        setSelectedItem={setSelectedItem}
                                        unit={unit}
                                        loadProductionData={LoadProductionData}
                                        isCustomerRepresentativeUser={isCustomerRepresentativeUser}
                                        isQCInspectorUser={isQCInspectorUser}
                                        isWelderUser={isWelderUser}
                                        currentUserTeamMemberId={currentUserTeamMemberId}
                                        isAdminOrSuperAdmin={isAdminOrSuperAdmin}
                                      />
                                    </>


                                  )}
                                </CardContent>
                              </Card>
                            </>
                          ) : (
                            <div className="flex items-center justify-center min-h-[700px]">
                              <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="w-16 h-16 bg-page-bg rounded-full flex items-center justify-center mb-4">
                                  <TableIcon className="h-8 w-8 text-heritage-grey" />
                                </div>
                                <h3 className="text-lg font-medium text-blueprint-blue mb-2">No Pipe Size Selected</h3>
                                <p className="text-heritage-grey max-w-sm">
                                  Please select a pipe size from the dropdown above to view production and weld data for that specific pipe configuration.
                                </p>
                              </div>
                            </div>
                          )}
                        </>
                          :
                          selectedPipe && selectedPipe.projectType == 'pipeline' && selectedPipe.isPumpHouseEnabled && isPumpHouseView ? (
                            <>
                              {selectedPipe.projectType && selectedPipe.projectType == 'pipeline' ? (
                                <Card>
                                  <CardContent className="p-4">
                                    {selectedPipe.isPumpHouseEnabled ? (
                                      /* Pump House enabled – full upload + table */
                                      <PumpHouseCoatingTable selectedPipe={selectedPipe} />
                                    ) : (
                                      /* Coating enabled, Pump House not enabled – empty card */
                                      <div className="flex items-center justify-center min-h-[400px]">
                                        <div className="flex flex-col items-center justify-center text-center gap-3">
                                          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                                            <TableIcon className="h-8 w-8 text-gray-400" />
                                          </div>
                                          <h3 className="text-lg font-medium text-gray-900">Coating</h3>
                                          <p className="text-gray-500 text-sm max-w-sm">
                                            No pump house configured for this pipe.
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                  </CardContent>
                                </Card>
                              ) : (
                                <div className="flex items-center justify-center min-h-[700px]">
                                  <div className="flex flex-col items-center justify-center py-12 text-center">
                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                      <TableIcon className="h-8 w-8 text-gray-400" />
                                    </div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Pipe Size Selected</h3>
                                    <p className="text-gray-500 max-w-sm">
                                      Please select a pipe size from the dropdown above to view coating data.
                                    </p>
                                  </div>
                                </div>
                              )}
                            </>
                          ) :
                            <div className="flex items-center justify-center min-h-[400px]">
                              <div className="flex flex-col items-center justify-center  text-center">
                                <div className="w-24 h-24 bg-page-bg rounded-full flex items-center justify-center mb-4">
                                  <img src={weldingImage} alt="Welding" className="h-20 w-20 text-heritage-grey" />
                                </div>
                                <h3 className="text-xl font-semibold text-blueprint-blue mb-3 leading-tight">
                                  Welding or coating Not Selected
                                </h3>
                                <p className="text-heritage-grey text-sm leading-relaxed">
                                  Welding functionality is not enabled for this project type.
                                </p>
                                <p className="text-heritage-grey text-sm leading-relaxed">
                                  This feature allows you to track weld data, monitor production progress, and ensure quality compliance throughout the welding process.
                                </p>
                              </div>
                            </div>
                        }
                      </>
                    )}
                  </>
                </CardContent>
              </Card>
            )}

            {/* Map View */}
            {isMapView && (
              <div className="w-full map-full-height">
                {selectedPipeSize !== undefined && pipeSizesData && pipeSizesData.length > 0 ? (
                  <ProductionMap project={project} selectedPipeSize={selectedPipeSize} selectedPipe={selectedPipe} statusCalculationMethod={statusCalculationMethod} statusCalculationStartedAt={statusCalculationStartedAt} canEdit={canEdit} />
                ) : (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                      <div className="text-heritage-grey mb-2">🗺️</div>
                      <p className="text-heritage-grey">Please select a pipe size to view the production map</p>
                    </div>
                  </div>
                )}
              </div>
            )}
            <>

              {/* Add the Weld Data Modal */}
              {selectedAssemblyForWeld && (
                <WeldPassList
                  isOpen={isWeldDataModalOpen}
                  onClose={handleCloseWeldData}
                  weld={{
                    id: 0,
                    weldNumber: selectedAssemblyForWeld,
                    weldIdentifier: selectedAssemblyForWeld,
                    wpsNumber: '',
                    status: 'Not Started',
                    weldTime: '-',
                    startTime: 0,
                    endTime: 0,
                    projectId: currentProject?.project?.projectId || 0,
                    projectName: currentProject?.project?.projectName || '',
                    statusByInstantaneous: 'NULL',
                    statusByAvgZone: 'NULL',
                    statusByAvgPass: 'NULL',
                    statusByAvgTilt: 'NULL',
                    gps: null,
                    minDistance: 0,
                    numOfGpsPoints: 0,
                    passes: []
                  }}
                  assemblyNumber={selectedAssemblyForWeld}
                  statusCalculationMethod={statusCalculationMethod}
                  passViewStatusMethod={statusCalculationMethod}
                  zoneViewStatusMethod={statusCalculationMethod}
                  tiltViewStatusMethod={statusCalculationMethod}
                  statusCalculationStartedAt={statusCalculationStartedAt}
                  canEdit={canEdit}
                  selectedPipe={selectedPipe}
                />
              )}
              <AssemblyProduction
                isOpen={isAssemblyModalOpen}
                onClose={() => {
                  setIsAssemblyModalOpen(false);
                  setSelectedAssembly(null);
                  setSelectedItem(null);
                  setLoadProductionData(true);
                }}
                onBack={() => {
                  setIsAssemblyModalOpen(false);
                  setSelectedAssembly(null);
                  setSelectedItem(null);
                  setLoadProductionData(true);
                }}
                project={project}
                selectedAssembly={selectedAssembly}
                setSelectedAssembly={setSelectedAssembly}
                materials={setupData}
                selectedPipe={selectedPipe}
                selectedItem={selectedItem}
                canEdit={canEdit}
                canReport={canReport}
                canSign={canSign}
                isCustomerRepresentativeUser={isCustomerRepresentativeUser}
                isQCInspectorUser={isQCInspectorUser}
                isWelderUser={isWelderUser}
                currentUserTeamMemberId={currentUserTeamMemberId}
                isAdminOrSuperAdmin={isAdminOrSuperAdmin}
                refreshMaterials={refreshMaterials}
              />

              {/* Add New Production Modal */}
              <AddNewProduction
                isOpen={isAddingProductionItem}
                onClose={() => setIsAddingProductionItem(false)}
                selectedPipeSize={selectedPipeSize}
                project={project}
              />

              {/* Status Configuration Modal */}
              <StatusConfigurationModal
                project={project}
                selectedPipeSize={selectedPipeSize}
                selectedPipe={selectedPipe}
                isOpen={isStatusConfigurationModalOpen}
                onClose={() => setIsStatusConfigurationModalOpen(false)}
                onStatusConfigSaved={setStatusCalculationStartedAt}
                canEdit={canEdit}
              />

              {/* Report Generation Modal */}
              {isReportGenerationModalOpen && <ReportGenerationModal
                isOpen={isReportGenerationModalOpen}
                onClose={() => setIsReportGenerationModalOpen(false)}
                onGenerateReport={(reportType: string, emails: string[]) => {
                  // Generating report
                  // Here you can implement the actual report generation logic
                }}
                projectName={project.project.projectName}
                jobNumber={project.project.projectNumber}
                weldNumbers={getFilteredWpsNumbers}
                projectId={parseInt(project?.project?.projectId) || undefined}
                selectedWeldNumbersForReport={selectedWeldNumbersForReport}
              />}

              {/* PQR Report Modal */}
              {isPqrReportModalOpen && (
                <PqrReportModal
                  isOpen={isPqrReportModalOpen}
                  onClose={() => {
                    setIsPqrReportModalOpen(false);
                    setPqrWeldNumbers([]);
                  }}
                  selectedWeldNumbers={pqrWeldNumbers}
                  projectId={parseInt(project?.project?.projectId) || undefined}
                  projectName={project.project.projectName}
                  project={project}
                  pipeSize={selectedPipe?.displayName}
                />
              )}

              {/* Welder Signature Report Modal - For Buckle Arrestor/Pipe with weld data */}
              {isWelderSignatureReportModalOpen &&
                <WelderSignatureReportModal
                  isOpen={isWelderSignatureReportModalOpen}
                  onClose={() => setIsWelderSignatureReportModalOpen(false)}
                  onGenerateReport={(reportType: string, welder1Signature: any, welder2Signature: any) => {
                    // Generating report with welder signatures
                    // Here you can implement the actual report generation logic with signatures
                    // console.log('Generating report with signatures:', {
                    //   reportType,
                    //   welder1Signature,
                    //   welder2Signature
                    // });
                  }}
                  projectName={project.project.projectName}
                  jobNumber={project.project.projectNumber}
                  weldNumbers={getFilteredWpsNumbers}
                  selectedPipe={selectedPipe}
                  selectedAssemblyData={selectedAssemblyForWeldData}
                  projectId={parseInt(project?.project?.projectId) || undefined}
                  selectedAssembly={selectedAssemblyForWeld ?? selectedAssembly ?? undefined}
                  assemblies={(() => {
                    if (!weldData || weldData.length === 0) return [];

                    // Filter weldData by filteredWeldIds if available, and ensure weldNumber exists
                    let filteredWelds = weldData.filter((weld: any) => {
                      // Must have weldNumber
                      if (!weld.weldNumber) return false;

                      // If filteredWeldIds is provided, only include welds in that list
                      if (filteredWeldIds.length > 0) {
                        return filteredWeldIds.includes(weld.weldNumber);
                      }

                      return true;
                    });
                    //  console.log('filteredWelds', filteredWelds);

                    // Map to create assemblies with two welds (Weld 1 and Weld 2)
                    return filteredWelds.map((weld: any) => ({
                      weldIdentifier: weld.weldNumber,
                      jobNumber: weld.job_number

                    }));
                  })()}
                  isAdminOrSuperAdmin={isAdminOrSuperAdmin}
                  currentUserTeamMemberId={currentUserTeamMemberId}
                  isCustomerRepresentativeUser={isCustomerRepresentativeUser}
                  isQCInspectorUser={isQCInspectorUser}
                  isWelderUser={isWelderUser}
                  canSign={canSign}
                  canEdit={canEdit}
                />}

              {/* Weld Data Modal */}
              <WeldDataModal
                isOpen={isWeldDataOpen}
                onClose={handleCloseWeldData}
                projectId={parseInt(project.project.projectId)}
                projectName={project.project.projectName}
                selectedPipe={selectedPipe}
                selectedPipeSize={selectedPipeSize}
                project={project}
                statusCalculationMethod={statusCalculationMethod}
                filteredWeldIds={filteredWeldIds}
                assemblyNumber={selectedAssemblyForWeld || ''}
                onFilteredDataChange={handleFilteredDataChange}
                onGenerateReport={() => {
                  const hasWelds =
                    (Array.isArray(weldData) && weldData.length > 0) || filteredWeldIds.length > 0;
                  if (!selectedPipe || !hasWelds) return;

                  if (
                    selectedPipe.projectSubType === 'buckleArrestor' ||
                    selectedPipe.projectType === 'pipeline'
                  ) {
                    setIsWelderSignatureReportModalOpen(true);
                  } else {
                    setIsReportGenerationModalOpen(true);
                  }
                }}
                canReport={canReport}
              />

              {/* Device Management Modal */}
              {isDeviceManagementModalOpen && <DeviceManagementModal
                isOpen={isDeviceManagementModalOpen}
                onClose={() => setIsDeviceManagementModalOpen(false)}
                assemblyNumber={selectedAssemblyForWeld || 'Device Management'}
                project={project}
                onOpenWeldParameters={handleOpenWeldParametersFromDownload}
              />
              }

              {isWeldParametersModalOpen && project?.project?.projectId ? (
                <CommonWeldParametersModal
                  isOpen={isWeldParametersModalOpen}
                  onClose={() => setIsWeldParametersModalOpen(false)}
                  projectId={parseInt(project.project.projectId)}
                  projectName={project.project.projectName ?? null}
                  canEdit={canEdit}
                />
              ) : null}

              {/* Pump House Coating Upload Dialog */}
              <UploadCoatingPumpHouseDialog
                isOpen={isPumpHouseCoatingModalOpen}
                onClose={() => setIsPumpHouseCoatingModalOpen(false)}
                setupData={setupData}
                project={project}
                selectedPipe={selectedPipe}
              />

              {/* Weld Data Upload — Manual uses folder/zip flow inside this same dialog */}
              <UploadWeldDataDialog
                isOpen={isWeldDataUploadModalOpen}
                onClose={() => setIsWeldDataUploadModalOpen(false)}
                project={project}
                onUploadComplete={reloadCurrentWeldData}
              />

              {/* TLOG Excel Upload Dialog — Excel/CSV → S/T/C + TLOG bulk (does not replace TXT upload) */}
              <UploadTlogExcelDataDialog
                isOpen={isTlogExcelUploadModalOpen}
                onClose={() => setIsTlogExcelUploadModalOpen(false)}
                project={project}
                onUploadComplete={() => loadWeldData(selectedPipe?.jobNumber?.jobNumbers || null, selectedPipe?.pipeId || null)}
              />

              <RenameJobNumberDialog
                open={isRenameJobNumberModalOpen}
                onOpenChange={setIsRenameJobNumberModalOpen}
                jobNumbers={availableJobNumbers}
                onConfirm={handleRenameJobNumber}
                loading={renameJobNumberLoading}
                projectId={project?.project?.projectId}
                projectName={project?.project?.projectName}
              />

              <NCRRegisterModal
                open={isNCRRegisterModalOpen}
                onClose={() => setIsNCRRegisterModalOpen(false)}
                project={project}
                projectId={project.project.projectId}
                pipeId={selectedPipe?.pipeId ?? null}
              />

              {/* Release Notes Modal */}
              {isReleaseNotesModalOpen && selectedPipe && (
                <ReleaseNotesModal
                  isOpen={isReleaseNotesModalOpen}
                  onClose={() => setIsReleaseNotesModalOpen(false)}
                  projectId={parseInt(project?.project?.projectId) || undefined}
                  projectName={project.project.projectName}
                  jobNumber={project.project.projectNumber}
                  initialType="pipe"
                  hideTypeSelection={true}
                  assemblies={assemblyData?.map((assembly: any) => ({
                    assemblyNumber: assembly.assembly_number || assembly.assemblyNumber || `Assembly-${assembly.production_id}`
                  })) || []}
                  selectedPipe={selectedPipe}
                />
              )}

              {isReleaseNotesViewModalOpen && (
                <IRNReportsViewModal
                  isOpen={isReleaseNotesViewModalOpen}
                  onClose={() => setIsReleaseNotesViewModalOpen(false)}
                  projectId={parseInt(project?.project?.projectId) || 0}
                  projectName={project.project.projectName}
                  jobNumber={project.project.projectNumber}
                />
              )}

              {/* Production Reports View Modal */}
              {isProductionReportViewModalOpen && (
                <ProductionReportsViewModal
                  isOpen={isProductionReportViewModalOpen}
                  onClose={() => setIsProductionReportViewModalOpen(false)}
                  projectId={parseInt(project?.project?.projectId) || 0}
                  projectName={project.project.projectName}
                  jobNumber={project.project.projectNumber}
                  pipeId={selectedPipe?.pipeId || undefined}
                />
              )}

              {/* Generate Production Report Modal */}
              {isProductionReportModalOpen && (
                <GenerateProductionReportModal
                  isOpen={isProductionReportModalOpen}
                  onClose={() => setProductionReportModalOpen(false)}
                  projectId={parseInt(project?.project?.projectId) || undefined}
                  projectName={project.project.projectName}
                  jobNumber={project.project.projectNumber}
                  selectedPipe={selectedPipe}
                  pipeSizesData={pipeSizesData}
                />
              )}
            </>
          </div>

        </>
      )}
    </div>
  );
};